import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { StepRenderer } from '../../../components/v2/StepRenderer';
import { ResourceToolkit } from '../../../components/v2/ResourceToolkit';
import { ExplainBroadcastContent } from '../../../components/v2/ExplainBroadcastContent';
import { MatchingResponseEditor, OrderingResponseEditor } from '../../../components/assessment/StructuredResponseEditors';
import { PacingTimerCard } from '../../../components/v2/PacingTimerCard';
import { LiveQuestionPanel } from '../../../components/v2/LiveQuestionPanel';
import { SimulationActivity } from '../../../components/v2/simulation/SimulationActivity';
import { createSimulationPreviewSession } from '../../../components/v2/simulation/simulationPreview';
import { HelpTab } from '../../../components/v2/student/HelpTab';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';
import { AppShell } from '../../../components/layout/AppShell';
import { studentV2NavItems, teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { useAuth } from '../../../hooks/useAuth';
import { hasStepContext } from '../../../hooks/v2/useHelpStepContext';
import { usePresentationStep } from '../../../hooks/v2/usePresentationStep';
import { formatStudioRecovery, useStudioDraft } from '../../../hooks/v2/useStudioDraft';
import { PreviewUnitReturnLink } from '../../../components/v2/teacher/PreviewUnitReturnLink';
import { UnitLiveReturnLink } from '../../../components/v2/student/UnitLiveBridge';
import { lessonRunsApi } from '../../../services/apiV2';
import { LessonStep, OfficialArtifactStatus } from '../../../types/v2';
import { matchingRowsToPairs, matchingValueText, normalizeMatchingDraftRows } from '../../../utils/matchingPairs';
import {
  DragDropMode,
  DragDropPrompt,
  DragDropSlotValue,
  DragTokenItem,
} from './dragdrop/DragDropRenderer';

interface RunPresentationProps {
  classroomRunId?: number;
  embeddedClassroom?: boolean;
  allowControlSummary?: boolean;
  studentPreview?: boolean;
  lessonPlanPreview?: boolean;
}

interface OverlayButtonDef {
  key: string;
  label: string;
  action_type: string;
}

interface OverlayRepairCard {
  vocabulary: string[];
  common_trap: string;
  self_check: string;
}

interface GuidedSequenceValidation {
  min_words?: number;
  min_chars?: number;
  max_chars?: number;
  must_start_with?: string;
}

interface GuidedSequenceCard {
  key: string;
  prompt: string;
  input_type: 'text' | 'textarea' | 'select';
  required: boolean;
  placeholder?: string;
  template?: string;
  options: Array<{ value: string; label: string }>;
  validation: GuidedSequenceValidation;
}

interface PreviousAnswersItem {
  step_id: number;
  label?: string;
  step_title?: string;
  response_text: string;
  submitted_at?: string | null;
  source?: string;
}

interface PreviousAnswersPayload {
  run_id: number;
  step_id: number;
  mode: 'off' | 'optional' | 'on' | string;
  title?: string;
  display?: string;
  allow_insert?: boolean;
  items: PreviousAnswersItem[];
}

interface StepDraftPayload {
  signalValue?: string;
  selectedSignalIndices?: number[];
  signalText?: string;
  guidedResponses?: Record<string, string>;
  reasoningText?: string;
  confidenceLevel?: number | null;
  showOptionalReasoning?: boolean;
  evidenceText?: string;
  evidenceLink?: string;
  matchingPairs?: Record<string, string>;
  orderingItems?: string[];
  dragDropPlacedTokens?: Array<string | null>;
}

const RUN_DRAFT_STORAGE_PREFIX = 'v2_run_presentation_drafts_v2';

const isStudentHelpEnabled = (step?: LessonStep | null): boolean => {
  const support = step?.content && typeof step.content === 'object' ? (step.content as Record<string, any>).support : null;
  return !(support && typeof support === 'object' && support.enabled === false);
};

const readDraftsForRun = (runId: number, owner: number): Record<string, StepDraftPayload> => {
  if (!runId || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`${RUN_DRAFT_STORAGE_PREFIX}:${owner}:${runId}`);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, StepDraftPayload>;
  } catch {
    return {};
  }
};

const writeDraftsForRun = (runId: number, owner: number, drafts: Record<string, StepDraftPayload>) => {
  if (!runId || typeof window === 'undefined') return;
  try {
    if (!drafts || Object.keys(drafts).length === 0) {
      window.localStorage.removeItem(`${RUN_DRAFT_STORAGE_PREFIX}:${owner}:${runId}`);
      return;
    }
    window.localStorage.setItem(`${RUN_DRAFT_STORAGE_PREFIX}:${owner}:${runId}`, JSON.stringify(drafts));
  } catch {
    // Ignore storage failures (private mode/quota/etc.)
  }
};

const getSignalOptionText = (option: any): string => (
  typeof option === 'string' ? option : String(option?.text || '')
);

export const V2RunPresentation: React.FC<RunPresentationProps> = ({ allowControlSummary = false, studentPreview = false, lessonPlanPreview = false, classroomRunId, embeddedClassroom = false }) => {
  const { runId, planId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const redesignEnabled = isUiRedesignPageEnabled('v2_student_run_presentation');
  const numericRunId = classroomRunId ?? (runId ? Number(runId) : undefined);
  const numericPlanId = planId ? Number(planId) : undefined;
  const isPlanPreview = Boolean(lessonPlanPreview && user?.role === 'teacher' && numericPlanId);
  const isStudentPreview = Boolean((studentPreview || lessonPlanPreview) && user?.role === 'teacher');
  const [planPreviewStepId, setPlanPreviewStepId] = useState<number | null>(null);
  const [presentationRefreshKey, setPresentationRefreshKey] = useState(0);
  const { data, loading, updating, error, lastUpdatedAt } = usePresentationStep(numericRunId, {
    allowControlSummary,
    refreshKey: presentationRefreshKey,
    lessonPlanId: isPlanPreview ? numericPlanId : undefined,
    previewStepId: isPlanPreview ? planPreviewStepId : null,
    simulationPreview: isStudentPreview,
  });
  const [relativeUpdated, setRelativeUpdated] = useState<string>('');
  const [signalValue, setSignalValue] = useState('');
  const [selectedSignalIndices, setSelectedSignalIndices] = useState<number[]>([]);
  const [signalText, setSignalText] = useState('');
  const [guidedResponses, setGuidedResponses] = useState<Record<string, string>>({});
  const [guidedCardIndex, setGuidedCardIndex] = useState(0);
  const [signalSubmitting, setSignalSubmitting] = useState(false);
  const [signalSubmitted, setSignalSubmitted] = useState(false);
  const [signalError, setSignalError] = useState('');
  const [qualityNotice, setQualityNotice] = useState('');
  const [evidenceText, setEvidenceText] = useState('');
  const [evidenceLink, setEvidenceLink] = useState('');
  const [evidenceSubmitting, setEvidenceSubmitting] = useState(false);
  const [evidenceSubmitted, setEvidenceSubmitted] = useState(false);
  const [evidenceError, setEvidenceError] = useState('');
  const [latestEvidence, setLatestEvidence] = useState<any | null>(null);
  const [evidencePrefilledStepId, setEvidencePrefilledStepId] = useState<number | null>(null);
  const [artifactActionSubmitting, setArtifactActionSubmitting] = useState<'drafting' | 'submitted' | null>(null);
  const [artifactError, setArtifactError] = useState('');
  const [artifactNotice, setArtifactNotice] = useState('');
  const [coachPanelOpen, setCoachPanelOpen] = useState(false);
  const [coachDraftText, setCoachDraftText] = useState('');
  const [coachDraftLink, setCoachDraftLink] = useState('');
  const [coachSubmitting, setCoachSubmitting] = useState(false);
  const [coachError, setCoachError] = useState('');
  const [coachNotice, setCoachNotice] = useState('');
  const [studentStepId, setStudentStepId] = useState<number | null>(null);
  const [currentStepRevealedOverride, setCurrentStepRevealedOverride] = useState<boolean | null>(null);
  const [myState, setMyState] = useState<any | null>(null);
  const [reasoningText, setReasoningText] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState<number | null>(null);
  const [showOptionalReasoning, setShowOptionalReasoning] = useState(false);
  const [dismissedBroadcastKeys, setDismissedBroadcastKeys] = useState<Record<string, true>>({});
  const [overlayAssistByKey, setOverlayAssistByKey] = useState<Record<string, string>>({});
  const [overlayExpandedByKey, setOverlayExpandedByKey] = useState<Record<string, boolean>>({});
  const [overlayAssistLoadingKey, setOverlayAssistLoadingKey] = useState<string | null>(null);
  const [overlayAssistError, setOverlayAssistError] = useState('');
  const [overlayReplyTextByKey, setOverlayReplyTextByKey] = useState<Record<string, string>>({});
  const [overlayReplySavingKey, setOverlayReplySavingKey] = useState<string | null>(null);
  const [overlayReplyErrorByKey, setOverlayReplyErrorByKey] = useState<Record<string, string>>({});
  const [overlayReplySavedByKey, setOverlayReplySavedByKey] = useState<Record<string, boolean>>({});
  const [matchingPairs, setMatchingPairs] = useState<Record<string, string>>({});
  const [dragDropSlots, setDragDropSlots] = useState<Array<DragDropSlotValue | null>>([]);
  const [previousAnswersData, setPreviousAnswersData] = useState<PreviousAnswersPayload | null>(null);
  const [previousAnswersLoading, setPreviousAnswersLoading] = useState(false);
  const [previousAnswersError, setPreviousAnswersError] = useState('');
  const [previousAnswersOpen, setPreviousAnswersOpen] = useState(false);
  const [studioHelpPanelOpen, setStudioHelpPanelOpen] = useState(false);
  const [studioHelpKind, setStudioHelpKind] = useState<'stuck' | 'dont_understand_prompt' | 'want_feedback' | 'technical_issue' | 'other'>('stuck');
  const [studioHelpNote, setStudioHelpNote] = useState('');
  const [studioHelpSubmitting, setStudioHelpSubmitting] = useState(false);
  const [studioHelpError, setStudioHelpError] = useState('');
  const [studioHelpNotice, setStudioHelpNotice] = useState('');
  const [studioNavLoadingStepId, setStudioNavLoadingStepId] = useState<number | null>(null);
  const [pendingStudioFinishAnnouncement, setPendingStudioFinishAnnouncement] = useState(false);
  const [previewStepSignal, setPreviewStepSignal] = useState<any | null>(null);
  const dragDropInstanceIdRef = useRef(1);
  const previousAnswersHydratedStepIdRef = useRef<number | null>(null);
  const orderingHydratedStepIdRef = useRef<number | null>(null);
  const orderingDirtyRef = useRef(false);
  const simulationPreviewSessionsRef = useRef<Record<string, ReturnType<typeof createSimulationPreviewSession>>>({});
  const signalHydrationKeyRef = useRef<string>('');
  const draftCacheRef = useRef<Record<string, StepDraftPayload>>({});
  const draftRunId = numericRunId && Number.isFinite(numericRunId) ? numericRunId : null;
  const handleGateError = useCallback((err: any) => {
    if (err?.response?.status === 428) {
      navigate('/v2/student', {
        replace: true,
        state: { gate: err?.response?.data?.gate || null },
      });
      return true;
    }
    return false;
  }, [navigate]);
  const requestPresentationRefresh = useCallback(() => {
    setPresentationRefreshKey((prev) => prev + 1);
  }, []);

  const step = data?.step || null;
  const allLessonSteps = Array.isArray((data as any)?.steps) ? ((data as any).steps as any[]) : [];
  const runStatus = data?.run?.status;
  const deliveryPhase = String((myState as any)?.delivery_phase || (data as any)?.run?.delivery_phase || 'live').trim().toLowerCase();
  const readinessRoute = ((myState as any)?.readiness_route && typeof (myState as any).readiness_route === 'object')
    ? (myState as any).readiness_route
    : null;
  const readinessRouteKey = String(readinessRoute?.route || '').trim().toLowerCase();
  const readinessRouteLabel = String(readinessRoute?.route_label || '').trim();
  const readinessRouteMessage = readinessRouteKey === 'go_test'
    ? 'Continue independently.'
    : readinessRouteKey === 'one_more_fix'
      ? 'Make another pass independently.'
      : readinessRouteKey === 'build_with_me'
        ? 'Stay with your teacher group.'
        : '';
  const studioStepIds = Array.isArray((myState as any)?.studio_step_ids)
    ? (myState as any).studio_step_ids.map((value: any) => Number(value)).filter((value: number) => Number.isInteger(value))
    : [];
  const studioStepLabelById = useMemo(() => {
    const labels: Record<number, string> = {};
    allLessonSteps.forEach((candidate: any) => {
      const stepId = Number(candidate?.id);
      if (!Number.isInteger(stepId)) return;
      const title = String(candidate?.title || '').trim();
      if (title) labels[stepId] = title;
    });
    return labels;
  }, [allLessonSteps]);
  const isStudioStep = step?.runner_phase === 'studio';
  const currentStepPathway = (step?.content as any)?.pathway;
  const currentPathwayType = String(currentStepPathway?.type || '').trim().toLowerCase().replace(/-/g, '_');
  const currentStepIsRouteOnlyPathway = Boolean(
    currentStepPathway
    && typeof currentStepPathway === 'object'
    && (
      currentStepPathway.route_only === true
      || (
        currentStepPathway.route_only !== false
        && currentStepPathway.teacher_routeable === true
        && ['support', 'extension', 'activation', 'teacher_table'].includes(currentPathwayType)
      )
    ),
  );
  const normalizedStepKind = String(step?.step_kind || '').trim().toLowerCase();
  const runIsTestMode = Boolean((data as any)?.run?.is_test_mode);
  const isAssessmentLikeStep = normalizedStepKind === 'assessment' || normalizedStepKind === 'exit_ticket';
  const isPracticeStep = normalizedStepKind === 'practice';
  const currentStudioProgress = ((myState as any)?.current_step_studio_progress ?? null) as any;
  const studioCompletion = ((myState as any)?.studio_completion ?? null) as any;
  const studioHelpRequest = ((myState as any)?.studio_help_request ?? null) as any;
  const evidenceConfig = step?.evidence_config;
  const signalConfig = step?.signal_config as any;
  const isSimulationActivity = String((step?.content as any)?.activity_type || '').trim().toLowerCase() === 'simulation';
  const rawSimulationPayload = ((myState as any)?.simulation ?? (data as any)?.simulation ?? null) as any;
  const simulationPayload = rawSimulationPayload
    && Number(rawSimulationPayload.step_id) === Number(step?.id)
    ? rawSimulationPayload
    : null;

  useEffect(() => {
    if (!isPlanPreview || planPreviewStepId || !step?.id) return;
    setPlanPreviewStepId(step.id);
  }, [isPlanPreview, planPreviewStepId, step?.id]);

  useEffect(() => {
    if (!draftRunId || !user?.id) {
      draftCacheRef.current = {};
      return;
    }
    draftCacheRef.current = readDraftsForRun(draftRunId, Number(user?.id));
  }, [draftRunId, user?.id]);

  const saveStepDraft = useCallback((stepId: number, draft: StepDraftPayload | null) => {
    if (!draftRunId || !user?.id || !stepId) return;
    const nextDrafts = { ...draftCacheRef.current };
    if (draft && Object.keys(draft).length > 0) {
      nextDrafts[String(stepId)] = draft;
    } else {
      delete nextDrafts[String(stepId)];
    }
    draftCacheRef.current = nextDrafts;
    writeDraftsForRun(draftRunId, Number(user?.id), nextDrafts);
  }, [draftRunId, user?.id]);
  const hasSignalConfig = useMemo(() => {
    if (isSimulationActivity) return false;
    if (!signalConfig) return false;
    if (typeof signalConfig === 'string') return signalConfig.trim().length > 0;
    if (typeof signalConfig === 'object' && !Array.isArray(signalConfig)) {
      return Object.keys(signalConfig).length > 0;
    }
    return false;
  }, [isSimulationActivity, signalConfig]);
  const hasPreviousAnswersConfig = useMemo(() => {
    if (!signalConfig || typeof signalConfig !== 'object' || Array.isArray(signalConfig)) return false;
    const config = (signalConfig as any).previous_answers ?? (signalConfig as any).previousAnswers;
    return Boolean(config && typeof config === 'object' && !Array.isArray(config));
  }, [signalConfig]);
  const isActualStudent = user?.role === 'student';
  const isStudentSurface = isActualStudent || isStudentPreview;
  const simulationPreviewKey = isSimulationActivity && step
    ? `${step.id}:${String((step.content as any)?.simulation?.case_id || '')}`
    : '';
  let simulationPreviewSession = simulationPreviewKey
    ? simulationPreviewSessionsRef.current[simulationPreviewKey]
    : null;
  if (isStudentPreview && simulationPreviewKey && simulationPreviewSession === undefined) {
    simulationPreviewSession = createSimulationPreviewSession(step!.id, step!.content, step!.signal_config);
    simulationPreviewSessionsRef.current[simulationPreviewKey] = simulationPreviewSession;
  }
  const myStepSignal = previewStepSignal ?? (data as any)?.my_step_signal ?? (myState as any)?.my_step_signal ?? null;
  const myStepSignalResponse = myStepSignal?.response;
  const revealMode = step?.signal_config?.reveal_mode ?? 'auto';
  const signalType = (step?.signal_config as any)?.signal_type || signalConfig?.type || '';
  const signalSubtype = (step?.signal_config as any)?.signal_type || '';
  const isQuickMcqSignal = signalSubtype === 'mcq_single' || signalSubtype === 'mcq_multi';
  const stepTitleClassName = isQuickMcqSignal ? 'text-2xl md:text-3xl' : undefined;
  const isMultiSelectSignal = signalType === 'mcq_multi' || Number((step?.signal_config as any)?.select || 0) > 1;
  const requiredSelections = (() => {
    const raw = Number((step?.signal_config as any)?.select || (isMultiSelectSignal ? 2 : 1));
    return Number.isFinite(raw) && raw > 0 ? raw : (isMultiSelectSignal ? 2 : 1);
  })();
  const currentStepRevealed = currentStepRevealedOverride ?? (data as any)?.current_step_revealed ?? false;
  const showBackToControl = user?.role === 'teacher' && (Boolean(runId) || isPlanPreview);
  const showBackToDashboard = user?.role === 'student';
  const teacherRunPath = isPlanPreview && planId
    ? `/v2/teacher/lesson-plan/${planId}/edit`
    : (runId ? `/v2/teacher/run/${runId}` : '/v2/teacher');
  const shellRole = user?.role === 'teacher' ? 'teacher' : 'student';
  const shellNavItems = shellRole === 'teacher' ? teacherV2NavItems('teach') : studentV2NavItems('work');
  const shellTitle = isStudentPreview ? 'Student Preview' : (shellRole === 'teacher' ? 'Run Presentation' : 'Lesson Presentation');
  const shellActions = showBackToControl ? (
    <Link
      to={teacherRunPath}
      className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-alt hover:text-text"
    >
      {isPlanPreview ? 'Back to Plan' : 'Back to Control'}
    </Link>
  ) : (
    showBackToDashboard ? (
      <Link
        to="/v2/student"
        className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-muted hover:bg-surface-alt hover:text-text"
      >
        Back to Dashboard
      </Link>
    ) : null
  );
  const canSubmit = isStudentSurface && runStatus === 'active';
  const currentStepGate = ((data as any)?.current_step_gate ?? (myState as any)?.current_step_gate) || null;
  const currentStepGateStatus = String(currentStepGate?.final_status || '').trim().toLowerCase();
  const currentStepGateReasons = Array.isArray(currentStepGate?.reason_codes)
    ? currentStepGate.reason_codes.map((item: any) => String(item || '').trim()).filter(Boolean)
    : [];
  const currentStepGateSuggestions = Array.isArray(currentStepGate?.suggestions)
    ? currentStepGate.suggestions.map((item: any) => String(item || '').trim()).filter(Boolean)
    : [];
  const gateBlocksProgress = currentStepGateStatus === 'blocked_revision' || currentStepGateStatus === 'needs_teacher_review';
  const gateAllowsProgress = currentStepGateStatus === 'allowed';
  const currentStepQuality = ((data as any)?.current_step_quality ?? (myState as any)?.current_step_quality) || null;
  const currentStepQualityStatus = String(currentStepQuality?.status || '').trim().toLowerCase();
  const currentStepQualityReasons = Array.isArray(currentStepQuality?.reason_codes)
    ? currentStepQuality.reason_codes.map((item: any) => String(item || '').trim()).filter(Boolean)
    : [];
  const currentStepQualitySuggestions = Array.isArray(currentStepQuality?.suggestions)
    ? currentStepQuality.suggestions.map((item: any) => String(item || '').trim()).filter(Boolean)
    : [];
  const qualityNeedsFix = currentStepQualityStatus === 'provisional' || currentStepQualityStatus === 'needs_teacher_review';
  const qualityResolved = currentStepQualityStatus === 'resolved';
  const fixQueueSummary = ((data as any)?.fix_queue_summary ?? (myState as any)?.fix_queue_summary) || null;
  const fixQueueUnresolved = Number(fixQueueSummary?.unresolved || 0);
  const isCurrentStep = !studentStepId
    ? !data?.run?.class_current_step_id || data.run.class_current_step_id === step?.id
    : studentStepId === step?.id;
  const isClassCurrentStep = !data?.run?.class_current_step_id || data.run.class_current_step_id === step?.id;
  const currentStepStartedAt = data?.run?.current_step_started_at
    || (data?.run as any)?.live_resource?.current_step_started_at
    || (myState as any)?.current_step_started_at
    || null;
  const currentStepTimer = ((data?.run as any)?.current_step_timer
    || (data?.run as any)?.live_resource?.pacing_timer
    || (myState as any)?.current_step_timer
    || null);
  const hasValidUdlContext = hasStepContext({
    runId: numericRunId ?? null,
    stepId: step?.id ?? null,
    step: step ?? null,
    runStatus: runStatus ?? '',
  });
  const isSelfDirectedLearningStep = Boolean(isStudioStep || deliveryPhase === 'studio' || isPracticeStep);
  const stepHelpEnabled = isStudentHelpEnabled(step);
  const showFullStepUdl = Boolean(
    isActualStudent
    && canSubmit
    && isCurrentStep
    && hasValidUdlContext
    && isSelfDirectedLearningStep
    && !isAssessmentLikeStep
    && !runIsTestMode
    && stepHelpEnabled
  );
  const showLimitedStepUdl = Boolean(
    isActualStudent
    && hasValidUdlContext
    && !showFullStepUdl
    && (
      !stepHelpEnabled
      ||
      !isSelfDirectedLearningStep
      || isAssessmentLikeStep
      || runIsTestMode
    )
  );
  const stepUdlLimitedMessage = !stepHelpEnabled
    ? 'Help is off for this step.'
    : runIsTestMode || isAssessmentLikeStep
    ? 'Help is off for this check.'
    : 'Help is off while your teacher is leading this step.';
  const currentArtifact = useMemo<OfficialArtifactStatus | null>(() => {
    const rawArtifact = (myState as any)?.current_step_artifact;
    if (rawArtifact && typeof rawArtifact === 'object' && Number.isFinite(Number((rawArtifact as any).step_id))) {
      return rawArtifact as OfficialArtifactStatus;
    }
    if (step?.artifact_requirement === 'apply_required') {
      return {
        step_id: step.id,
        step_title: step.title,
        stage_tag: step.stage_tag,
        artifact_requirement: 'apply_required',
        artifact_location: step.artifact_location || 'none',
        artifact_config: step.artifact_config || {},
        coach_mode: step.coach_mode || 'off',
        status: 'not_started',
        continuity_mode: false,
        is_current_step: true,
      };
    }
    return null;
  }, [myState, step]);
  const artifactStepId = currentArtifact?.step_id ?? null;
  const isExternalArtifact = Boolean(currentArtifact && currentArtifact.artifact_location === 'headrush');
  const isInternalArtifact = Boolean(
    currentArtifact
    && currentArtifact.artifact_location === 'elevate_internal'
    && artifactStepId === step?.id,
  );
  const isAuthoredAssessmentItem = Boolean(
    typeof (step?.content as any)?.assessment_item_type === 'string'
    && String((step?.content as any).assessment_item_type).trim(),
  );
  const isLegacyEvidenceStep = Boolean(
    step?.counts_as_evidence
    && !isAuthoredAssessmentItem
    && step?.artifact_requirement !== 'apply_required',
  );
  const shouldShowExternalArtifactCard = Boolean(
    isStudentSurface && !isSimulationActivity && isExternalArtifact && artifactStepId,
  );
  const shouldShowInternalArtifactCard = Boolean(
    isStudentSurface && !isSimulationActivity && isInternalArtifact && artifactStepId,
  );
  const shouldShowLegacyEvidenceCard = Boolean(
    isStudentSurface && !isSimulationActivity && isLegacyEvidenceStep && step?.id,
  );
  const evidenceStepId = shouldShowInternalArtifactCard
    ? artifactStepId
    : (shouldShowLegacyEvidenceCard ? step?.id ?? null : null);
  const isFinalArtifactCard = shouldShowExternalArtifactCard || shouldShowInternalArtifactCard;
  const artifactConfig = currentArtifact?.artifact_config && typeof currentArtifact.artifact_config === 'object'
    ? currentArtifact.artifact_config
    : {};
  const artifactPrompt = typeof artifactConfig.prompt === 'string' ? artifactConfig.prompt : '';
  const artifactExternalUrl = typeof artifactConfig.external_url === 'string'
    ? artifactConfig.external_url
    : (typeof currentArtifact?.source_url === 'string' ? currentArtifact.source_url : '');
  const artifactExternalAssignmentId = typeof artifactConfig.external_assignment_id === 'string'
    ? artifactConfig.external_assignment_id
    : (typeof artifactConfig.external_assignment_id === 'number'
      ? String(artifactConfig.external_assignment_id)
      : (typeof currentArtifact?.source_ref === 'string' ? currentArtifact.source_ref : ''));
  const artifactStatusLabel = useMemo(() => {
    switch (currentArtifact?.status) {
      case 'drafting':
        return 'Drafting';
      case 'coaching_flagged':
        return 'Needs draft fixes';
      case 'ready_to_submit':
        return 'Ready to submit';
      case 'submitted':
        return 'Submitted';
      case 'under_review':
        return 'Under review';
      case 'approved':
        return 'Approved';
      case 'needs_revision':
        return 'Needs revision';
      default:
        return 'Not started';
    }
  }, [currentArtifact?.status]);
  const artifactStatusClassName = useMemo(() => {
    switch (currentArtifact?.status) {
      case 'approved':
        return 'bg-emerald-100 text-emerald-700';
      case 'submitted':
      case 'under_review':
        return 'bg-sky-100 text-sky-700';
      case 'needs_revision':
      case 'coaching_flagged':
        return 'bg-rose-100 text-rose-700';
      case 'ready_to_submit':
        return 'bg-indigo-100 text-indigo-700';
      default:
        return 'bg-amber-100 text-amber-700';
    }
  }, [currentArtifact?.status]);
  const shouldShowArtifactContinuityCopy = Boolean(
    currentArtifact
    && (
      Boolean(currentArtifact.continuity_mode)
      || runStatus === 'completed'
      || (artifactStepId !== null && artifactStepId !== step?.id)
    ),
  );
  const broadcast = myState?.broadcast;
  const latestCoachSession = currentArtifact?.latest_coach_session ?? null;
  const isCoachLinkValid = coachDraftLink.trim().length === 0 || /^https?:\/\//i.test(coachDraftLink.trim());
  const canRunDraftCoach = Boolean(
    numericRunId
    && artifactStepId
    && currentArtifact?.coach_mode === 'optional'
    && currentArtifact?.status !== 'submitted'
    && currentArtifact?.status !== 'approved'
    && currentArtifact?.status !== 'under_review'
    && (coachDraftText.trim().length > 0 || coachDraftLink.trim().length > 0),
  );
  const broadcastKey = String(broadcast?.version ?? broadcast?.created_at ?? '');
  const showExplainBroadcast = Boolean(
    broadcast &&
    broadcast?.kind === 'explain' &&
    Number(broadcast?.step_id) === Number(step?.id) &&
    broadcastKey &&
    !dismissedBroadcastKeys[broadcastKey],
  );
  const overlayModeRaw = String((myState as any)?.overlay_mode || '').trim().toLowerCase();
  const overlayMode = overlayModeRaw === 'repair' || overlayModeRaw === 'extend' ? overlayModeRaw : null;
  const overlayButtons = Array.isArray((myState as any)?.overlay_buttons)
    ? ((myState as any).overlay_buttons as any[])
      .map((item: any) => ({
        key: String(item?.key || '').trim(),
        label: String(item?.label || '').trim(),
        action_type: String(item?.action_type || '').trim(),
      }))
      .filter((item: OverlayButtonDef) => item.key && item.label && item.action_type)
    : [];
  const overlayStepId = Number((myState as any)?.overlay_step_id);
  const overlayRepairCard = useMemo<OverlayRepairCard | null>(() => {
    const raw = (myState as any)?.overlay_repair_card;
    if (!raw || typeof raw !== 'object') return null;
    const vocabularyRaw = Array.isArray((raw as any).vocabulary) ? (raw as any).vocabulary : [];
    const vocabulary = vocabularyRaw
      .map((item: any) => String(item || '').trim())
      .filter((item: string) => Boolean(item))
      .slice(0, 6);
    const commonTrap = String((raw as any).common_trap || '').trim();
    const selfCheck = String((raw as any).self_check || '').trim();
    if (!vocabulary.length && !commonTrap && !selfCheck) return null;
    return {
      vocabulary,
      common_trap: commonTrap,
      self_check: selfCheck,
    };
  }, [myState]);
  const showOverlayCard = Boolean(
    isActualStudent
    && overlayMode
    && Number.isFinite(overlayStepId)
    && Number(step?.id) === overlayStepId
    && overlayButtons.length > 0,
  );

  const evidenceInstructions = useMemo(() => {
    if (!evidenceConfig) return null;
    if (typeof evidenceConfig === 'string') return evidenceConfig;
    if (typeof evidenceConfig?.prompt === 'string') return evidenceConfig.prompt;
    if (typeof evidenceConfig?.instructions === 'string') return evidenceConfig.instructions;
    return null;
  }, [evidenceConfig]);

  const signalPrompt = useMemo(() => {
    if (!signalConfig) return '';
    if (typeof signalConfig === 'string') return signalConfig;
    if (typeof signalConfig?.prompt === 'string') return signalConfig.prompt;
    if (typeof signalConfig?.question === 'string') return signalConfig.question;
    if (typeof step?.content?.prompt_md === 'string') return step.content.prompt_md;
    return '';
  }, [signalConfig, step?.content?.prompt_md]);
  const normalizedSignalTypeFromConfig = String(signalConfig?.signal_type || signalConfig?.type || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  const isGuidedSequenceSignal = normalizedSignalTypeFromConfig === 'guided_sequence';
  const guidedSubmitLabel = typeof signalConfig?.submit_label === 'string'
    ? signalConfig.submit_label
    : 'Submit';
  const liveResponseHeading = useMemo(() => {
    if (isGuidedSequenceSignal) {
      return String(signalConfig?.title || 'Reasoning check');
    }
    switch (step?.live_response_mode) {
      case 'guided_response':
        return 'Reasoning check';
      case 'practice_checkpoint':
        return 'Practice checkpoint';
      default:
        return 'Quick check';
    }
  }, [isGuidedSequenceSignal, signalConfig?.title, step?.live_response_mode]);
  const guidedSequenceCards = useMemo<GuidedSequenceCard[]>(() => {
    if (!isGuidedSequenceSignal) return [];
    const cardsRaw = Array.isArray(signalConfig?.cards) ? signalConfig.cards : [];
    return cardsRaw
      .map((card: any, idx: number) => {
        if (!card || typeof card !== 'object') return null;
        const key = String(card.key || '').trim();
        if (!key) return null;
        const prompt = String(card.prompt || `Prompt ${idx + 1}`).trim();
        const inputTypeRaw = String(card.input_type || 'text').trim().toLowerCase();
        const inputType = inputTypeRaw === 'textarea' || inputTypeRaw === 'select' ? inputTypeRaw : 'text';
        const required = Boolean(card.required);
        const placeholder = typeof card.placeholder === 'string' ? card.placeholder : '';
        const template = typeof card.template === 'string' ? card.template : '';
        const options = (Array.isArray(card.options) ? card.options : [])
          .map((option: any) => {
            if (option && typeof option === 'object') {
              const value = String(option.value ?? '').trim();
              const label = String(option.label ?? option.value ?? '').trim();
              return value && label ? { value, label } : null;
            }
            const value = String(option ?? '').trim();
            return value ? { value, label: value } : null;
          })
          .filter((option: { value: string; label: string } | null): option is { value: string; label: string } => Boolean(option));
        const validation = (card.validation && typeof card.validation === 'object' && !Array.isArray(card.validation))
          ? card.validation
          : {};
        return {
          key,
          prompt,
          input_type: inputType,
          required,
          placeholder,
          template,
          options,
          validation,
        };
      })
      .filter((card: GuidedSequenceCard | null): card is GuidedSequenceCard => Boolean(card));
  }, [isGuidedSequenceSignal, signalConfig?.cards]);
  const currentGuidedCard = guidedSequenceCards[Math.max(0, Math.min(guidedCardIndex, guidedSequenceCards.length - 1))] || null;
  const reasoningGateConfig = signalConfig?.reasoning_gate;
  const reasoningGateModeRaw = typeof reasoningGateConfig?.mode === 'string'
    ? reasoningGateConfig.mode.trim().toLowerCase()
    : '';
  const reasoningGateMode = (
    reasoningGateModeRaw === 'required' || reasoningGateModeRaw === 'optional' || reasoningGateModeRaw === 'off'
  ) ? reasoningGateModeRaw : (hasSignalConfig ? (isGuidedSequenceSignal ? 'off' : 'required') : 'off');
  const rawReasoningMinChars = Number(reasoningGateConfig?.min_chars);
  const reasoningMinChars = Number.isInteger(rawReasoningMinChars) && rawReasoningMinChars >= 0
    ? rawReasoningMinChars
    : 15;
  const reasoningRequired = reasoningGateMode === 'required';
  const reasoningOptional = reasoningGateMode === 'optional';
  const reasoningEnabled = reasoningGateMode !== 'off';
  const shouldShowReasoningTextarea = reasoningRequired || (reasoningOptional && showOptionalReasoning);
  const confidenceRequired = typeof signalConfig?.confidence_required === 'boolean'
    ? signalConfig.confidence_required
    : !isGuidedSequenceSignal;
  const sourceItem = (step?.content as any)?.source_item ?? (step?.content as any)?.sourceItem ?? null;
  const sourceType = String((sourceItem as any)?.type || '').trim().toLowerCase().replace(/-/g, '_');
  const signalKind = String(
    (step?.content as any)?.source_item?.type
    ?? (step?.content as any)?.sourceItem?.type
    ?? (step?.signal_config as any)?.type
    ?? (step as any)?.signalConfig?.type
    ?? '',
  ).trim().toLowerCase().replace(/-/g, '_');
  const signalTypeNormalized = normalizedSignalTypeFromConfig;
  const isMcqSignal = (
    signalKind === 'mcq'
    || signalKind === 'mcq_single'
    || signalKind === 'mcq_multi'
    || signalTypeNormalized === 'mcq'
    || signalTypeNormalized === 'mcq_single'
    || signalTypeNormalized === 'mcq_multi'
  );
  const isMatchingSignal = (
    signalKind === 'matching'
    || signalKind === 'pairs'
    || sourceType === 'matching'
    || sourceType === 'pairs'
    || signalTypeNormalized === 'matching'
    || signalTypeNormalized === 'pairs'
  );
  const isOrderingSignal = signalKind === 'ordering';
  const isDragDropCodeSignal = (
    signalKind === 'drag_drop_code'
    || signalKind === 'drag_drop_fill_blanks'
    || sourceType === 'drag_drop_code'
    || sourceType === 'drag_drop_fill_blanks'
    || signalTypeNormalized === 'drag_drop_code'
    || signalTypeNormalized === 'drag_drop_fill_blanks'
  );
  const matchingPairRows = useMemo(
    () => normalizeMatchingDraftRows((sourceItem as any)?.pairs),
    [sourceItem],
  );
  const matchingLeftItems: string[] = (() => {
    const fromSource = Array.isArray((sourceItem as any)?.left_items)
      ? (sourceItem as any).left_items.map((item: any) => matchingValueText(item).trim()).filter((item: string) => item.length > 0)
      : [];
    if (fromSource.length > 0) return fromSource;
    return matchingPairRows.map((row) => row.left.trim()).filter((item) => item.length > 0);
  })();
  const matchingRightItems: string[] = (() => {
    const fromSource = Array.isArray((sourceItem as any)?.right_items)
      ? (sourceItem as any).right_items.map((item: any) => matchingValueText(item).trim()).filter((item: string) => item.length > 0)
      : [];
    if (fromSource.length > 0) return fromSource;
    return Array.from(new Set(
      matchingPairRows
        .map((row) => row.right.trim())
        .filter((item: string) => item.length > 0),
    ));
  })();
  const cleanedMatchingPairs = useMemo(
    () => Object.entries(matchingPairs).reduce((acc: Record<string, string>, [left, right]) => {
      const cleanLeft = String(left ?? '').trim();
      const cleanRight = String(right ?? '').trim();
      if (cleanLeft && cleanRight) {
        acc[cleanLeft] = cleanRight;
      }
      return acc;
    }, {}),
    [matchingPairs],
  );
  const isStructuredStep = sourceType === 'ordering'
    || isMatchingSignal
    || sourceType === 'drag_drop_code'
    || sourceType === 'drag_drop_fill_blanks';
  const dragDropTokens = isDragDropCodeSignal
    ? (Array.isArray((sourceItem as any)?.tokens)
      ? (sourceItem as any).tokens
      : (Array.isArray((sourceItem as any)?.options)
        ? (sourceItem as any).options
        : (Array.isArray((sourceItem as any)?.token_bank) ? (sourceItem as any).token_bank : [])))
      .map((token: any) => String(token ?? '').trim())
      .filter((token: string) => token.length > 0)
    : [];
  const dragDropTarget = isDragDropCodeSignal
    ? String((sourceItem as any)?.target || (sourceItem as any)?.code || (sourceItem as any)?.template || '')
    : '';
  const dragDropPromptLines = isDragDropCodeSignal
    ? (Array.isArray((sourceItem as any)?.prompt_lines)
      ? (sourceItem as any).prompt_lines.map((line: any) => String(line ?? ''))
      : dragDropTarget
        .split('\n')
        .map((line: string) => line.replace(/\r/g, ''))
        .filter((line: string) => line.trim().length > 0))
    : [];
  const dragDropSegments = dragDropTarget ? dragDropTarget.split('____') : [];
  const targetBlankCount = Math.max(dragDropSegments.length - 1, 0);
  const explicitBlankCountRaw = Number((sourceItem as any)?.blanks);
  const explicitBlankCount = Number.isInteger(explicitBlankCountRaw) && explicitBlankCountRaw > 0 ? explicitBlankCountRaw : 0;
  const answerTokenBlankCount = Array.isArray((sourceItem as any)?.answer_tokens) ? (sourceItem as any).answer_tokens.length : 0;
  const tokenBankBlankCount = dragDropTokens.length;
  const dragDropBlankCount =
    explicitBlankCount > 0
      ? explicitBlankCount
      : answerTokenBlankCount > 0
        ? answerTokenBlankCount
        : targetBlankCount > 0
          ? targetBlankCount
          : tokenBankBlankCount > 0
            ? tokenBankBlankCount
            : 0;
  const dragDropHasBlanksInTarget = Boolean(dragDropTarget && dragDropTarget.includes('____'));
  const dragDropIsOrderingLine = isDragDropCodeSignal
    && !dragDropHasBlanksInTarget
    && String(dragDropTarget).trim().length > 0;
  const dragDropMode: DragDropMode = (() => {
    if (dragDropIsOrderingLine) return 'code_order_line';
    if (signalTypeNormalized === 'drag_drop_code' || sourceType === 'drag_drop_code') {
      return 'code_inline';
    }
    if (dragDropPromptLines.length > 1 && dragDropBlankCount >= dragDropPromptLines.length) {
      return 'row_match';
    }
    return 'text_inline';
  })();
  const markdownComponents = useMemo(
    () => ({
      p({ children }: any) {
        return <p className="my-2 leading-relaxed">{children}</p>;
      },
      ul({ children }: any) {
        return <ul className="my-2 list-disc pl-6 space-y-1">{children}</ul>;
      },
      ol({ children }: any) {
        return <ol className="my-2 list-decimal pl-6 space-y-1">{children}</ol>;
      },
      li({ children }: any) {
        return <li className="leading-relaxed">{children}</li>;
      },
      strong({ children }: any) {
        return <strong className="font-semibold">{children}</strong>;
      },
      em({ children }: any) {
        return <em className="italic">{children}</em>;
      },
      code({ inline, children }: any) {
        return !inline ? (
          <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
            <code>{children}</code>
          </pre>
        ) : (
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[0.9em] text-slate-900">{children}</code>
        );
      },
    }),
    [],
  );
  const dragDropFinal = useMemo(() => {
    if (!isDragDropCodeSignal || dragDropBlankCount <= 0) return '';

    if (dragDropHasBlanksInTarget) {
      return dragDropSegments.reduce((acc, segment, idx) => {
        const token = dragDropSlots[idx]?.token || '';
        if (idx < dragDropBlankCount) {
          return `${acc}${segment}${token}`;
        }
        return `${acc}${segment}`;
      }, '');
    }

    const prefix = String(dragDropTarget || '').trim();
    const placedTokens = dragDropSlots
      .slice(0, dragDropBlankCount)
      .map((slot) => slot?.token || '')
      .filter((t) => t.length > 0);

    return [prefix, ...placedTokens].filter(Boolean).join(' ').trim();
  }, [
    isDragDropCodeSignal,
    dragDropBlankCount,
    dragDropHasBlanksInTarget,
    dragDropSegments,
    dragDropSlots,
    dragDropTarget,
  ]);
  const dragDropPlacedTokensForSubmit = useMemo(
    () => dragDropSlots.slice(0, dragDropBlankCount).map((slot) => slot?.token ?? null),
    [dragDropSlots, dragDropBlankCount],
  );
  const [orderingItems, setOrderingItems] = useState<string[]>([]);

  useEffect(() => {
    if (!isOrderingSignal) {
      setOrderingItems([]);
      orderingHydratedStepIdRef.current = null;
      orderingDirtyRef.current = false;
      return;
    }
    const responseRaw = myStepSignalResponse?.ordering ?? myStepSignalResponse?.items ?? myStepSignalResponse?.value;
    if (Array.isArray(responseRaw)) return;
    if (orderingDirtyRef.current) return;
    const items = Array.isArray((sourceItem as any)?.items)
      ? (sourceItem as any).items.map((item: any) => String(item ?? ''))
      : [];
    setOrderingItems(items);
    orderingHydratedStepIdRef.current = null;
  }, [isOrderingSignal, step?.id, myStepSignalResponse, sourceItem]);

  useEffect(() => {
    orderingDirtyRef.current = false;
  }, [step?.id, user?.id]);

  useEffect(() => {
    if (!isOrderingSignal || !step?.id) return;
    if (orderingHydratedStepIdRef.current === step.id) return;
    const raw = myStepSignalResponse?.ordering ?? myStepSignalResponse?.items ?? myStepSignalResponse?.value;
    if (Array.isArray(raw)) {
      setOrderingItems(raw.map((item: any) => String(item ?? '')));
      orderingHydratedStepIdRef.current = step.id;
    }
  }, [isOrderingSignal, step?.id, myStepSignalResponse]);

  const dragDropHydrationPlacedTokens = useMemo(() => {
    const placed = myStepSignalResponse?.placed_tokens;
    return Array.isArray(placed) ? placed : [];
  }, [myStepSignalResponse]);

  const shouldSuppressStepPromptMd = useMemo(() => {
    if (!isQuickMcqSignal) return false;
    const stepPromptMd = typeof step?.content?.prompt_md === 'string' ? step.content.prompt_md : '';
    if (!stepPromptMd || !signalPrompt) return false;
    const normalize = (value: string) => value.trim().replace(/\s+/g, ' ');
    return normalize(stepPromptMd) === normalize(signalPrompt);
  }, [isQuickMcqSignal, step?.content?.prompt_md, signalPrompt]);

  const stepContentForRenderer = useMemo(() => {
    if (isStructuredStep) {
      const baseContent = step?.content;
      if (!baseContent || typeof baseContent !== 'object') return baseContent;
      const { source_item, sourceItem, ...restContent } = baseContent as Record<string, any>;
      return {
        ...restContent,
        prompt_md: '',
      };
    }
    if (!shouldSuppressStepPromptMd) return step?.content;
    const baseContent = step?.content;
    if (!baseContent || typeof baseContent !== 'object') return baseContent;
    return {
      ...baseContent,
      prompt_md: '',
    };
  }, [shouldSuppressStepPromptMd, isStructuredStep, step?.content]);
  const availableResources = Array.isArray((data as any)?.resources) ? (data as any).resources : [];
  const refreshedPresentation = (myState as any)?.presentation;
  const initialPresentation = (data as any)?.presentation;
  const hasRefreshedPresentation = Boolean(myState && Object.prototype.hasOwnProperty.call(myState, 'presentation'));
  const presentationCandidate = hasRefreshedPresentation ? refreshedPresentation : initialPresentation;
  const studentPresentation = presentationCandidate && typeof presentationCandidate === 'object'
    ? presentationCandidate
    : null;
  const mediaLifecycleKey = `${numericRunId ?? 'preview'}:${step?.id ?? 'none'}`;

  useEffect(() => {
    setSignalValue('');
    setSelectedSignalIndices([]);
    setSignalText('');
    setGuidedResponses({});
    setGuidedCardIndex(0);
    setSignalSubmitted(false);
    setSignalError('');
    setEvidenceText('');
    setEvidenceLink('');
    setEvidenceSubmitted(false);
    setEvidenceError('');
    setLatestEvidence(null);
    setEvidencePrefilledStepId(null);
    setReasoningText('');
    setConfidenceLevel(null);
    setShowOptionalReasoning(false);
    setMatchingPairs({});
    setDragDropSlots([]);
    setPreviousAnswersData(null);
    setPreviousAnswersLoading(false);
    setPreviousAnswersError('');
    setPreviousAnswersOpen(false);
    setArtifactActionSubmitting(null);
    setArtifactError('');
    setArtifactNotice('');
    setCoachPanelOpen(false);
    setCoachDraftText('');
    setCoachDraftLink('');
    setCoachSubmitting(false);
    setCoachError('');
    setCoachNotice('');
    setPreviewStepSignal(null);
    previousAnswersHydratedStepIdRef.current = null;
    signalHydrationKeyRef.current = '';
  }, [step?.id, user?.id]);

  useEffect(() => {
    setCoachPanelOpen(false);
    setCoachDraftText('');
    setCoachDraftLink('');
    setCoachSubmitting(false);
    setCoachError('');
    setCoachNotice('');
  }, [artifactStepId]);

  useEffect(() => {
    if (!isStudentSurface || !step?.id || !draftRunId || !user?.id || isStudioStep) return;
    const stepDraft = draftCacheRef.current[String(step.id)];
    if (!stepDraft || typeof stepDraft !== 'object') return;

    if (typeof stepDraft.signalValue === 'string') {
      setSignalValue(stepDraft.signalValue);
    }
    if (Array.isArray(stepDraft.selectedSignalIndices)) {
      const nextSelected = stepDraft.selectedSignalIndices
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value >= 0);
      if (nextSelected.length > 0) {
        setSelectedSignalIndices(Array.from(new Set(nextSelected)).sort((a, b) => a - b));
      }
    }
    if (typeof stepDraft.signalText === 'string') {
      setSignalText(stepDraft.signalText);
    }
    if (stepDraft.guidedResponses && typeof stepDraft.guidedResponses === 'object' && !Array.isArray(stepDraft.guidedResponses)) {
      const nextGuided = Object.entries(stepDraft.guidedResponses).reduce((acc: Record<string, string>, [key, value]) => {
        const cleanKey = String(key || '').trim();
        if (!cleanKey) return acc;
        acc[cleanKey] = typeof value === 'string' ? value : String(value ?? '');
        return acc;
      }, {});
      if (Object.keys(nextGuided).length > 0) {
        setGuidedResponses(nextGuided);
      }
    }
    if (typeof stepDraft.reasoningText === 'string') {
      setReasoningText(stepDraft.reasoningText);
    }
    if (stepDraft.confidenceLevel !== undefined && stepDraft.confidenceLevel !== null) {
      const parsedConfidence = Number(stepDraft.confidenceLevel);
      if (Number.isFinite(parsedConfidence)) {
        setConfidenceLevel(parsedConfidence);
      }
    }
    if (stepDraft.showOptionalReasoning === true) {
      setShowOptionalReasoning(true);
    }
    if (typeof stepDraft.evidenceText === 'string') {
      setEvidenceText(stepDraft.evidenceText);
    }
    if (typeof stepDraft.evidenceLink === 'string') {
      setEvidenceLink(stepDraft.evidenceLink);
    }
    if (stepDraft.matchingPairs && typeof stepDraft.matchingPairs === 'object' && !Array.isArray(stepDraft.matchingPairs)) {
      const nextPairs = matchingRowsToPairs(normalizeMatchingDraftRows(stepDraft.matchingPairs));
      if (Object.keys(nextPairs).length > 0) {
        setMatchingPairs(nextPairs);
      }
    }
    if (Array.isArray(stepDraft.orderingItems) && stepDraft.orderingItems.length > 0) {
      setOrderingItems(stepDraft.orderingItems.map((item) => String(item ?? '')));
      orderingHydratedStepIdRef.current = step.id;
    }
    if (isDragDropCodeSignal && dragDropBlankCount > 0 && Array.isArray(stepDraft.dragDropPlacedTokens)) {
      const nextSlots: Array<DragDropSlotValue | null> = Array.from({ length: dragDropBlankCount }, (_, idx) => {
        const token = String(stepDraft.dragDropPlacedTokens?.[idx] ?? '').trim();
        if (!token) return null;
        return { instanceId: dragDropInstanceIdRef.current++, token };
      });
      if (nextSlots.some((slot) => Boolean(slot?.token))) {
        setDragDropSlots(nextSlots);
      }
    }

    const hasDraftEvidenceText = typeof stepDraft.evidenceText === 'string' && stepDraft.evidenceText.trim().length > 0;
    const hasDraftEvidenceLink = typeof stepDraft.evidenceLink === 'string' && stepDraft.evidenceLink.trim().length > 0;
    if (hasDraftEvidenceText || hasDraftEvidenceLink) {
      setEvidencePrefilledStepId(step.id);
    }
  }, [isStudentSurface, step?.id, draftRunId, user?.id, isStudioStep, isDragDropCodeSignal, dragDropBlankCount]);

  useEffect(() => {
    if (!isDragDropCodeSignal) return;
    if (dragDropBlankCount <= 0) {
      setDragDropSlots([]);
      return;
    }
    setDragDropSlots((prev) => {
      const next = Array.from({ length: dragDropBlankCount }, (_, idx) => (idx < prev.length ? prev[idx] : null));
      return next;
    });
  }, [isDragDropCodeSignal, dragDropBlankCount, step?.id]);

  useEffect(() => {
    setDismissedBroadcastKeys({});
  }, [step?.id, user?.id]);

  useEffect(() => {
    if (!step?.id) return;
    if (myStepSignal?.lesson_step_id && myStepSignal.lesson_step_id !== step.id) return;
    const response = myStepSignalResponse;
    if (!response) return;
    const responseKey = `${step.id}:${JSON.stringify(response)}`;
    if (signalHydrationKeyRef.current === responseKey) return;
    const hasLocalGuidedResponse = Object.values(guidedResponses).some((value) => String(value || '').trim().length > 0);
    const hasLocalMatchingPairs = Object.keys(cleanedMatchingPairs).length > 0;
    const hasLocalDragDropState = dragDropSlots.some((slot) => Boolean(slot?.token && slot.token.trim().length > 0));
    const hasLocalPrimarySignalState = Boolean(
      hasLocalGuidedResponse
      || selectedSignalIndices.length > 0
      || String(signalValue || '').trim().length > 0
      || String(signalText || '').trim().length > 0
      || hasLocalMatchingPairs
      || hasLocalDragDropState
    );
    const hasLocalSupplementalSignalState = Boolean(
      String(reasoningText || '').trim().length > 0
      || confidenceLevel !== null
      || showOptionalReasoning
    );
    if (hasLocalPrimarySignalState || hasLocalSupplementalSignalState) return;
    if (isGuidedSequenceSignal) {
      const nextGuidedResponses: Record<string, string> = {};
      for (const card of guidedSequenceCards) {
        const value = response[card.key];
        nextGuidedResponses[card.key] = typeof value === 'string' ? value : (value == null ? '' : String(value));
      }
      setGuidedResponses(nextGuidedResponses);
    } else if (isMcqSignal) {
      if (isMultiSelectSignal) {
        const raw = response.selected_indices ?? response.value;
        let parsed: number[] = [];
        if (Array.isArray(raw)) {
          parsed = raw
            .map((item: any) => Number(item))
            .filter((num: number) => Number.isInteger(num) && num >= 0);
        } else if (typeof raw === 'string') {
          parsed = raw
            .replace(/\s+/g, ',')
            .split(',')
            .map((part) => Number(part.trim()))
            .filter((num) => Number.isInteger(num) && num >= 0);
        }
        setSelectedSignalIndices(Array.from(new Set(parsed)).sort((a, b) => a - b));
      } else {
        setSignalValue(response.value ?? '');
      }
    } else {
      if (isMatchingSignal) {
        const rawPairs = response.pairs ?? response.value;
        if (rawPairs && typeof rawPairs === 'object' && !Array.isArray(rawPairs)) {
          const nextPairs = Object.entries(rawPairs).reduce((acc: Record<string, string>, [left, right]) => {
            const cleanLeft = String(left ?? '').trim();
            const cleanRight = String(right ?? '').trim();
            if (cleanLeft && cleanRight) {
              acc[cleanLeft] = cleanRight;
            }
            return acc;
          }, {});
          setMatchingPairs(nextPairs);
        }
      } else if (isDragDropCodeSignal) {
        if (typeof response.value === 'string') {
          setSignalText(response.value ?? '');
        }
      } else {
        setSignalText(response.value ?? '');
      }
    }
    if (typeof response.reasoning_text === 'string') {
      setReasoningText(response.reasoning_text);
      if (response.reasoning_text.trim().length > 0) {
        setShowOptionalReasoning(true);
      }
    }
    if (response.confidence_level !== undefined && response.confidence_level !== null) {
      const parsed = Number(response.confidence_level);
      setConfidenceLevel(Number.isNaN(parsed) ? null : parsed);
    }
    setSignalSubmitted(true);
    signalHydrationKeyRef.current = responseKey;
  }, [
    step?.id,
    myStepSignal,
    myStepSignalResponse,
    guidedResponses,
    isGuidedSequenceSignal,
    guidedSequenceCards,
    isMcqSignal,
    isMatchingSignal,
    isOrderingSignal,
    isMultiSelectSignal,
    isDragDropCodeSignal,
    cleanedMatchingPairs,
    confidenceLevel,
    dragDropBlankCount,
    dragDropSlots,
    dragDropTokens,
    reasoningText,
    selectedSignalIndices,
    showOptionalReasoning,
    signalText,
    signalValue,
  ]);

  useEffect(() => {
    if (!isDragDropCodeSignal || dragDropBlankCount <= 0) return;
    if (dragDropHydrationPlacedTokens.length === 0) return;
    // Only hydrate when we have nothing locally yet (e.g., refresh) or step changed.
    // Prevents polling/myState refresh from overwriting a student's in-progress edits.
    const hasLocalAny = dragDropSlots.some((s) => Boolean(s?.token && s.token.trim().length > 0));
    if (hasLocalAny) return;
    const nextSlots: Array<DragDropSlotValue | null> = Array.from({ length: dragDropBlankCount }, (_, idx) => {
      const token = String(dragDropHydrationPlacedTokens[idx] ?? '').trim();
      if (!token) return null;
      return { instanceId: dragDropInstanceIdRef.current++, token };
    });
    setDragDropSlots(nextSlots);
  }, [isDragDropCodeSignal, dragDropBlankCount, dragDropHydrationPlacedTokens, step?.id, dragDropSlots]);

  const currentStepDraftPayload = useMemo<StepDraftPayload>(() => {
    const normalizedGuidedResponses = Object.entries(guidedResponses).reduce((acc: Record<string, string>, [key, value]) => {
      const cleanKey = String(key || '').trim();
      const cleanValue = String(value ?? '');
      if (cleanKey && cleanValue.trim().length > 0) {
        acc[cleanKey] = cleanValue;
      }
      return acc;
    }, {});
    const orderedDraftItems = orderingItems
      .map((item) => String(item ?? ''))
      .filter((item) => item.trim().length > 0);
    const dragDropPlacedTokens = dragDropSlots
      .slice(0, dragDropBlankCount)
      .map((slot) => (slot?.token ? slot.token : null));
    const hasDragDropDraft = dragDropPlacedTokens.some((token) => String(token ?? '').trim().length > 0);

    const nextDraft: StepDraftPayload = {};
    if (signalValue.trim().length > 0) nextDraft.signalValue = signalValue;
    if (selectedSignalIndices.length > 0) nextDraft.selectedSignalIndices = selectedSignalIndices;
    if (signalText.trim().length > 0) nextDraft.signalText = signalText;
    if (Object.keys(normalizedGuidedResponses).length > 0) nextDraft.guidedResponses = normalizedGuidedResponses;
    if (reasoningText.trim().length > 0) nextDraft.reasoningText = reasoningText;
    if (confidenceLevel !== null) nextDraft.confidenceLevel = confidenceLevel;
    if (showOptionalReasoning) nextDraft.showOptionalReasoning = true;
    if (evidenceText.trim().length > 0) nextDraft.evidenceText = evidenceText;
    if (evidenceLink.trim().length > 0) nextDraft.evidenceLink = evidenceLink;
    if (Object.keys(cleanedMatchingPairs).length > 0) nextDraft.matchingPairs = cleanedMatchingPairs;
    if (orderedDraftItems.length > 0) nextDraft.orderingItems = orderedDraftItems;
    if (hasDragDropDraft) nextDraft.dragDropPlacedTokens = dragDropPlacedTokens;
    return nextDraft;
  }, [
    cleanedMatchingPairs,
    confidenceLevel,
    dragDropBlankCount,
    dragDropSlots,
    evidenceLink,
    evidenceText,
    guidedResponses,
    orderingItems,
    reasoningText,
    selectedSignalIndices,
    showOptionalReasoning,
    signalText,
    signalValue,
  ]);

  const studioActiveStepIndex = useMemo(
    () => (step?.id ? studioStepIds.findIndex((stepId: number) => stepId === step.id) : -1),
    [step?.id, studioStepIds],
  );
  const nextStudioStepId = studioActiveStepIndex >= 0 && studioActiveStepIndex < studioStepIds.length - 1
    ? studioStepIds[studioActiveStepIndex + 1]
    : null;
  const isLastStudioStep = Boolean(isStudioStep && studioActiveStepIndex >= 0 && studioActiveStepIndex === studioStepIds.length - 1);
  const shouldAutoAdvanceStudio = Boolean(isStudioStep && isCurrentStep && nextStudioStepId && !currentStepIsRouteOnlyPathway);
  const shouldFinishStudioOnSubmit = Boolean(isStudioStep && isCurrentStep && isLastStudioStep && !nextStudioStepId);
  const studioCurrentStepSubmitted = Boolean(studioCompletion?.current_step_submitted);
  const studioStepNeedsSubmission = Boolean(
    step?.artifact_requirement === 'apply_required'
    || step?.counts_as_evidence
    || (step?.live_response_mode && step.live_response_mode !== 'none')
  );
  const canFinishInformationalStudioStep = Boolean(
    isActualStudent
    && deliveryPhase === 'studio'
    && isLastStudioStep
    && !studioStepNeedsSubmission
    && !studioCurrentStepSubmitted
  );
  const studioFullyComplete = Boolean(studioCompletion?.fully_complete_including_artifact_expectations);
  const studioArtifactStillNeeded = Boolean(
    studioCompletion?.studio_tail_complete_excluding_official_artifact
    && !studioFullyComplete,
  );
  const showStudioFinishedPanel = Boolean(
    isActualStudent
    && deliveryPhase === 'studio'
    && isLastStudioStep
    && studioCurrentStepSubmitted
    && studioFullyComplete,
  );

  const restoreStudioDraft = useCallback((draft: Record<string, any>) => {
    setSignalValue(draft.signalValue || '');
    setSelectedSignalIndices(draft.selectedSignalIndices || []);
    setSignalText(draft.signalText || '');
    setGuidedResponses(draft.guidedResponses || {});
    setReasoningText(draft.reasoningText || '');
    setConfidenceLevel(draft.confidenceLevel ?? null);
    setShowOptionalReasoning(Boolean(draft.showOptionalReasoning));
    setEvidenceText(draft.evidenceText || '');
    setEvidenceLink(draft.evidenceLink || '');
    setMatchingPairs(draft.matchingPairs || {});
    setOrderingItems(draft.orderingItems || (Array.isArray((sourceItem as any)?.items) ? (sourceItem as any).items.map(String) : []));
    orderingDirtyRef.current = Boolean(draft.orderingItems);
    orderingHydratedStepIdRef.current = step?.id ?? null;
    setDragDropSlots(Array.from({ length: dragDropBlankCount }, (_, index) => {
      const token = draft.dragDropPlacedTokens?.[index];
      return token ? { instanceId: dragDropInstanceIdRef.current++, token } : null;
    }));
  }, [step?.id, sourceItem, dragDropBlankCount]);
  const studioDraft = useStudioDraft(user?.id, numericRunId, step?.id,
    isActualStudent && isStudioStep, currentStepDraftPayload, restoreStudioDraft, studioCurrentStepSubmitted);

  useEffect(() => {
    if (!isStudentSurface || !step?.id || !draftRunId || !user?.id || isStudioStep) return;

    if (Object.keys(currentStepDraftPayload).length === 0) {
      saveStepDraft(step.id, null);
      return;
    }
    saveStepDraft(step.id, currentStepDraftPayload);
  }, [
    currentStepDraftPayload,
    isStudentSurface,
    step?.id,
    draftRunId,
    saveStepDraft,
    isStudioStep,
  ]);

  useEffect(() => {
    const loadLatestEvidence = async () => {
      if (!numericRunId || !evidenceStepId || !isActualStudent) {
        setLatestEvidence(null);
        return;
      }
      try {
        const response = await lessonRunsApi.getLatestEvidence(numericRunId, evidenceStepId);
        setLatestEvidence(response || null);
        if (response && evidencePrefilledStepId !== evidenceStepId) {
          const content = response.content || {};
          setEvidenceText(typeof content.text === 'string' ? content.text : '');
          setEvidenceLink(typeof content.url === 'string' ? content.url : '');
          setEvidenceSubmitted(response.status === 'submitted' || response.status === 'approved');
          setEvidencePrefilledStepId(evidenceStepId);
        }
      } catch (err: any) {
        if (handleGateError(err)) return;
        setLatestEvidence(null);
      }
    };
    loadLatestEvidence();
  }, [numericRunId, evidenceStepId, canSubmit, evidencePrefilledStepId, studentStepId, isActualStudent, handleGateError]);

  useEffect(() => {
    let isActive = true;
    const loadPreviousAnswers = async () => {
      if (!numericRunId || !isActualStudent || !step?.id || !hasPreviousAnswersConfig) {
        if (!isActive) return;
        setPreviousAnswersData(null);
        setPreviousAnswersLoading(false);
        setPreviousAnswersError('');
        return;
      }
      setPreviousAnswersLoading(true);
      setPreviousAnswersError('');
      try {
        const payload = await lessonRunsApi.previousAnswers(numericRunId, step.id);
        if (!isActive) return;
        const normalized: PreviousAnswersPayload = {
          run_id: Number(payload?.run_id || numericRunId),
          step_id: Number(payload?.step_id || step.id),
          mode: String(payload?.mode || 'optional'),
          title: typeof payload?.title === 'string' ? payload.title : '',
          display: typeof payload?.display === 'string' ? payload.display : '',
          allow_insert: Boolean(payload?.allow_insert),
          items: Array.isArray(payload?.items) ? payload.items : [],
        };
        setPreviousAnswersData(normalized);
        if (previousAnswersHydratedStepIdRef.current !== step.id) {
          setPreviousAnswersOpen(String(normalized.mode || '').trim().toLowerCase() === 'on');
          previousAnswersHydratedStepIdRef.current = step.id;
        }
      } catch (err: any) {
        if (!isActive) return;
        if (handleGateError(err)) return;
        setPreviousAnswersData(null);
        setPreviousAnswersOpen(false);
        setPreviousAnswersError(err?.response?.data?.error || 'Could not load previous answers.');
      } finally {
        if (isActive) {
          setPreviousAnswersLoading(false);
        }
      }
    };
    void loadPreviousAnswers();
    return () => {
      isActive = false;
    };
  }, [numericRunId, isActualStudent, step?.id, hasPreviousAnswersConfig, handleGateError]);

  const reloadStudentState = useCallback(async () => {
    if (!numericRunId || !isActualStudent) return;
    try {
      const payload = await lessonRunsApi.myState(numericRunId);
      setMyState(payload ?? null);
      const directStep = payload?.my_current_step_id
        ?? payload?.current_step_id
        ?? payload?.student_state?.current_step_id
        ?? payload?.class_current_step;
      const nextStepId = typeof directStep === 'number' ? directStep : null;
      setStudentStepId((currentStepId) => currentStepId === nextStepId ? currentStepId : nextStepId);
      if (typeof payload?.current_step_revealed === 'boolean') {
        setCurrentStepRevealedOverride(payload.current_step_revealed);
      }
    } catch (err: any) {
      if (handleGateError(err)) return;
      // ignore load errors
    }
  }, [numericRunId, isActualStudent, handleGateError]);

  const continueToNextStudioStep = useCallback(async (
    onError: (message: string) => void,
    fallbackMessage = 'Saved, but we could not open the next independent-work step. Use the Independent Work step buttons above to continue.',
  ) => {
    if (!shouldAutoAdvanceStudio || !numericRunId || !nextStudioStepId) return false;
    try {
      await lessonRunsApi.navigateStudio(numericRunId, nextStudioStepId);
      setStudentStepId(nextStudioStepId);
      return true;
    } catch (navigationError: any) {
      if (handleGateError(navigationError)) return true;
      onError(navigationError?.response?.data?.error || fallbackMessage);
      return false;
    }
  }, [shouldAutoAdvanceStudio, numericRunId, nextStudioStepId, handleGateError]);

  useEffect(() => {
    if (!pendingStudioFinishAnnouncement || !showStudioFinishedPanel) return;
    if (typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setPendingStudioFinishAnnouncement(false);
  }, [pendingStudioFinishAnnouncement, showStudioFinishedPanel]);

  useEffect(() => {
    if (!numericRunId || !isActualStudent) return;
    let isMounted = true;
    const markEnter = async () => {
      try {
        await lessonRunsApi.markPresence(numericRunId, 'enter');
      } catch {
        // ignore transient presence ping failures
      }
    };
    const markHeartbeat = async () => {
      try {
        await lessonRunsApi.markPresence(numericRunId, 'heartbeat');
      } catch {
        // ignore transient presence ping failures
      }
    };

    void markEnter();
    const interval = window.setInterval(() => {
      if (!isMounted || document.visibilityState === 'hidden') return;
      void markHeartbeat();
    }, 20000);

    return () => {
      isMounted = false;
      window.clearInterval(interval);
    };
  }, [numericRunId, isActualStudent]);

  useEffect(() => {
    if (!numericRunId || !isActualStudent) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      reloadStudentState();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [numericRunId, isActualStudent, reloadStudentState]);

  useEffect(() => {
    if (!numericRunId || !isActualStudent) return;
    let active = true;
    const refreshPresentation = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const payload = await lessonRunsApi.presentationState(numericRunId);
        if (!active) return;
        setMyState((previous: any) => ({ ...(previous || {}), presentation: payload?.presentation ?? null }));
      } catch {
        // Keep the last rendered slide during a transient network failure.
      }
    };
    void refreshPresentation();
    const interval = window.setInterval(() => { void refreshPresentation(); }, 2500);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [numericRunId, isActualStudent]);

  useEffect(() => {
    let isActive = true;
    const loadStudentState = async () => {
      if (!numericRunId || !isActualStudent || !user?.id) {
        if (isActive) {
          setStudentStepId(null);
        }
        return;
      }
      await reloadStudentState();
    };
    void loadStudentState();
    return () => {
      isActive = false;
    };
  }, [numericRunId, isActualStudent, user?.id, data?.run?.class_current_step_id, reloadStudentState]);

  useEffect(() => {
    if (!numericRunId || !isActualStudent) return;
    void reloadStudentState();
  }, [numericRunId, isActualStudent, step?.id, reloadStudentState]);

  useEffect(() => {
    const normalizedKind = String(studioHelpRequest?.kind || '').trim();
    if (
      normalizedKind === 'stuck'
      || normalizedKind === 'dont_understand_prompt'
      || normalizedKind === 'want_feedback'
      || normalizedKind === 'technical_issue'
      || normalizedKind === 'other'
    ) {
      setStudioHelpKind(normalizedKind as 'stuck' | 'dont_understand_prompt' | 'want_feedback' | 'technical_issue' | 'other');
    }
    setStudioHelpNote(typeof studioHelpRequest?.note === 'string' ? studioHelpRequest.note : '');
    setStudioHelpError('');
  }, [studioHelpRequest?.kind, studioHelpRequest?.note, step?.id]);

  useEffect(() => {
    setOverlayAssistByKey({});
    setOverlayExpandedByKey({});
    setOverlayAssistLoadingKey(null);
    setOverlayAssistError('');
  }, [step?.id, user?.id]);

  useEffect(() => {
    setQualityNotice('');
  }, [step?.id, user?.id]);

  useEffect(() => {
    if (revealMode !== 'teacher' || !myStepSignal || currentStepRevealed || !numericRunId) return;
    const interval = window.setInterval(async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        await reloadStudentState();
      } catch {
        // ignore polling errors
      }
    }, 6000);
    return () => window.clearInterval(interval);
  }, [revealMode, myStepSignal, currentStepRevealed, numericRunId, reloadStudentState]);

  useEffect(() => {
    if (!lastUpdatedAt) return;
    const computeLabel = () => {
      const seconds = Math.floor((Date.now() - lastUpdatedAt.getTime()) / 1000);
      if (seconds < 10) {
        setRelativeUpdated('Updated just now');
      } else if (seconds < 60) {
        setRelativeUpdated(`Last updated ${seconds}s ago`);
      } else {
        const timeString = lastUpdatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setRelativeUpdated(`Updated ${timeString}`);
      }
    };
    computeLabel();
    const interval = window.setInterval(computeLabel, 1000);
    return () => window.clearInterval(interval);
  }, [lastUpdatedAt]);

  useEffect(() => {
    if (!isGuidedSequenceSignal) {
      setGuidedCardIndex(0);
      return;
    }
    if (guidedSequenceCards.length === 0) {
      setGuidedCardIndex(0);
      return;
    }
    setGuidedCardIndex((prev) => {
      if (prev < 0) return 0;
      if (prev >= guidedSequenceCards.length) return guidedSequenceCards.length - 1;
      return prev;
    });
  }, [isGuidedSequenceSignal, guidedSequenceCards.length]);

  const getGuidedCardError = useCallback((card: GuidedSequenceCard, rawValue: string) => {
    const value = String(rawValue || '').trim();
    const prompt = card.prompt || card.key;
    const validation = (card.validation && typeof card.validation === 'object')
      ? card.validation
      : {};

    if (card.required && !value) return `${prompt} is required.`;
    if (!value) return '';

    const minWordsRaw = Number(validation.min_words);
    const minWords = Number.isInteger(minWordsRaw) && minWordsRaw > 0 ? minWordsRaw : 0;
    if (minWords > 0) {
      const wordCount = value.split(/\s+/).filter(Boolean).length;
      if (wordCount < minWords) return `${prompt} must be at least ${minWords} words.`;
    }

    const minCharsRaw = Number(validation.min_chars);
    const minChars = Number.isInteger(minCharsRaw) && minCharsRaw > 0 ? minCharsRaw : 0;
    if (minChars > 0 && value.length < minChars) {
      return `${prompt} must be at least ${minChars} characters.`;
    }

    const maxCharsRaw = Number(validation.max_chars);
    const maxChars = Number.isInteger(maxCharsRaw) && maxCharsRaw > 0 ? maxCharsRaw : 0;
    if (maxChars > 0 && value.length > maxChars) {
      return `${prompt} must be at most ${maxChars} characters.`;
    }

    const mustStartWith = typeof validation.must_start_with === 'string'
      ? validation.must_start_with.trim()
      : '';
    if (mustStartWith && !value.toLowerCase().startsWith(mustStartWith.toLowerCase())) {
      return `${prompt} must start with "${mustStartWith}".`;
    }
    return '';
  }, []);

  const trimmedReasoningText = reasoningText.trim();
  const hasValidReasoning = !shouldShowReasoningTextarea || trimmedReasoningText.length >= reasoningMinChars;
  const confidenceSelected = confidenceLevel !== null;

  const submitSignal = async () => {
    if (!step) return;
    if (!canSubmit || !isCurrentStep) {
      await reloadStudentState();
      setSignalError('This step is no longer active for you. We refreshed your lesson view.');
      return;
    }
    if (confidenceRequired && !confidenceSelected) {
      setSignalError('Select your confidence before submitting.');
      return;
    }
    if (!hasValidReasoning) {
      setSignalError('Complete the reasoning gate before submitting.');
      return;
    }
    if (isGuidedSequenceSignal && guidedSequenceCards.length === 0) {
      setSignalError('No guided sequence cards are configured for this step.');
      return;
    }
    if (isGuidedSequenceSignal) {
      for (let cardIdx = 0; cardIdx < guidedSequenceCards.length; cardIdx += 1) {
        const card = guidedSequenceCards[cardIdx];
        const candidate = guidedResponses[card.key] || '';
        const errorMessage = getGuidedCardError(card, candidate);
        if (errorMessage) {
          setGuidedCardIndex(cardIdx);
          setSignalError(errorMessage);
          return;
        }
      }
    }
    setSignalSubmitting(true);
    setSignalError('');
    try {
      const response = isGuidedSequenceSignal
        ? guidedSequenceCards.reduce((acc, card) => {
          acc[card.key] = String(guidedResponses[card.key] || '').trim();
          return acc;
        }, {} as Record<string, string>)
        : (isMcqSignal
          ? (isMultiSelectSignal ? { selected_indices: selectedSignalIndices } : { value: signalValue })
          : (isOrderingSignal
            ? { type: 'ordering', ordering: orderingItems, value: orderingItems }
            : (isMatchingSignal
              ? {
                  type: 'matching',
                  pairs: cleanedMatchingPairs,
                  value: cleanedMatchingPairs,
                }
              : (isDragDropCodeSignal
              ? {
                  type: sourceType,
                  placed_tokens: dragDropPlacedTokensForSubmit,
                  final: dragDropFinal,
                }
              : { value: signalText }))));
      const payload: Record<string, any> = { ...response };
      if (confidenceRequired) {
        payload.confidence_level = confidenceLevel;
      }
      if (reasoningRequired || (reasoningOptional && showOptionalReasoning && trimmedReasoningText.length > 0)) {
        payload.reasoning_text = reasoningText;
      }
      if (isStudentPreview) {
        const options = Array.isArray((step.signal_config as any)?.options) ? (step.signal_config as any).options : [];
        const selectedIndexForPreview = !isMultiSelectSignal && isMcqSignal
          ? options.findIndex((option: any) => getSignalOptionText(option) === String(signalValue || ''))
          : -1;
        setPreviewStepSignal({
          lesson_step_id: step.id,
          response: payload,
          selected_index: selectedIndexForPreview >= 0 ? selectedIndexForPreview : null,
          selected_value: selectedIndexForPreview >= 0 ? options[selectedIndexForPreview] : undefined,
        });
        setSignalSubmitted(true);
        setQualityNotice('Preview response recorded locally. No student data or reports were changed.');
        saveStepDraft(step.id, null);
        return;
      }
      if (!numericRunId) return;
      const signalResult = await lessonRunsApi.submitSignal(numericRunId, step.id, { response: payload });
      const qualityPayload = signalResult?.data?.quality || null;
      const qualityStatus = String(qualityPayload?.status || '').trim().toLowerCase();
      if (qualityStatus === 'provisional' || qualityStatus === 'needs_teacher_review') {
        setQualityNotice(String(qualityPayload?.student_feedback || 'Saved. This response is in your fix queue for later revision.'));
      } else if (qualityStatus === 'resolved') {
        setQualityNotice(String(qualityPayload?.student_feedback || 'Saved. Your latest revision cleared a previous fix queue item.'));
      } else if (qualityStatus === 'accepted') {
        setQualityNotice('Saved. Your response met quality checks.');
      }
      setSignalSubmitted(true);
      saveStepDraft(step.id, null);
      if (await continueToNextStudioStep(
        setSignalError,
        'Response saved, but we could not open the next independent-work step. Use the Independent Work step buttons above to continue.',
      )) {
        setSignalSubmitted(false);
      } else if (shouldFinishStudioOnSubmit) {
        setPendingStudioFinishAnnouncement(true);
      }
      await reloadStudentState();
      requestPresentationRefresh();
    } catch (err: any) {
      if (handleGateError(err)) return;
      const stepLockError = String(err?.response?.data?.error || '');
      if (stepLockError.toLowerCase().includes('signals may only be submitted for')) {
        await reloadStudentState();
        setSignalError('Your active step changed while you were working. Please submit on your current step.');
        return;
      }
      const fieldErrors = err?.response?.data?.field_errors;
      if (fieldErrors && typeof fieldErrors === 'object') {
        const firstMessage = Object.values(fieldErrors).find((value) => typeof value === 'string') as string | undefined;
        if (firstMessage) {
          setSignalError(firstMessage);
          return;
        }
      }
      setSignalError(err?.response?.data?.error || 'Failed to submit response');
    } finally {
      setSignalSubmitting(false);
    }
  };

  const isEvidenceLink = evidenceConfig?.type === 'link';
  const evidenceValue = isEvidenceLink ? evidenceLink.trim() : evidenceText.trim();
  const isValidEvidenceLink = !isEvidenceLink || /^https?:\/\//i.test(evidenceValue);
  const inRunSubmissionCopy = useMemo(() => {
    if (shouldShowInternalArtifactCard) {
      return {
        latestLabel: 'Latest saved artifact',
        placeholder: isEvidenceLink ? 'https://' : 'Share your final artifact',
        submitButtonLabel: latestEvidence ? 'Submit updated artifact' : 'Submit artifact',
        reasoningError: 'Complete the reasoning gate before submitting this artifact.',
        qualityFixDefault: 'Saved. This artifact is in your fix queue for later revision.',
        qualityAccepted: 'Saved. Your artifact met quality checks.',
        signalFallback: 'Saved your artifact, but reasoning could not be submitted.',
        submitError: 'Failed to submit artifact',
      };
    }
    return {
      latestLabel: 'Latest legacy evidence',
      placeholder: isEvidenceLink ? 'https://' : 'Share your response for this legacy evidence step',
      submitButtonLabel: latestEvidence ? 'Submit updated legacy evidence' : 'Submit legacy evidence',
      reasoningError: 'Complete the reasoning gate before submitting this legacy evidence.',
      qualityFixDefault: 'Saved. This legacy evidence is in your fix queue for later revision.',
      qualityAccepted: 'Saved. Your legacy evidence met quality checks.',
      signalFallback: 'Saved your legacy evidence, but reasoning could not be submitted.',
      submitError: 'Failed to submit legacy evidence',
    };
  }, [isEvidenceLink, latestEvidence, shouldShowInternalArtifactCard]);

  const submitEvidence = async () => {
    if (!evidenceStepId) return;
    if (reasoningRequired && !hasValidReasoning) {
      setEvidenceError(inRunSubmissionCopy.reasoningError);
      return;
    }
    if (!evidenceValue) return;
    if (isEvidenceLink && !isValidEvidenceLink) {
      setEvidenceError('Enter a valid URL starting with http:// or https://');
      return;
    }
    if (isStudentPreview) {
      const previewEvidence = {
        id: 0,
        status: 'preview',
        content: isEvidenceLink ? { url: evidenceValue } : { text: evidenceValue },
        submitted_at: new Date().toISOString(),
      };
      setLatestEvidence(previewEvidence);
      setEvidenceSubmitted(true);
      setQualityNotice('Preview artifact saved locally. No student evidence or reports were changed.');
      if (evidenceStepId) {
        setEvidencePrefilledStepId(evidenceStepId);
      }
      return;
    }
    if (!numericRunId) return;
    setEvidenceSubmitting(true);
    setEvidenceError('');
    try {
      const evidenceResult = await lessonRunsApi.submitEvidence(numericRunId, {
        lesson_step_id: evidenceStepId,
        evidence_type: isEvidenceLink ? 'file' : 'text',
        content: isEvidenceLink ? { url: evidenceValue } : { text: evidenceValue },
      });
      const qualityPayload = evidenceResult?.quality || null;
      const qualityStatus = String(qualityPayload?.status || '').trim().toLowerCase();
      if (qualityStatus === 'provisional' || qualityStatus === 'needs_teacher_review') {
        setQualityNotice(String(qualityPayload?.student_feedback || inRunSubmissionCopy.qualityFixDefault));
      } else if (qualityStatus === 'resolved') {
        setQualityNotice(String(qualityPayload?.student_feedback || 'Saved. Your revision resolved a previous fix queue item.'));
      } else if (qualityStatus === 'accepted') {
        setQualityNotice(inRunSubmissionCopy.qualityAccepted);
      }
      setEvidenceSubmitted(true);
      if (reasoningRequired && step?.id) {
        const alreadySubmitted = Boolean(signalSubmitted);
        if (!alreadySubmitted) {
          try {
            await lessonRunsApi.submitSignal(numericRunId, step.id, {
              response: {
                value: 'evidence_submitted',
                reasoning_text: reasoningText,
                confidence_level: confidenceLevel,
              },
            });
            setSignalSubmitted(true);
          } catch (err: any) {
            if (handleGateError(err)) return;
            setSignalError(err?.response?.data?.error || inRunSubmissionCopy.signalFallback);
          }
        }
      }
      if (evidenceStepId) {
        setEvidencePrefilledStepId(evidenceStepId);
      }
      try {
        const refreshed = await lessonRunsApi.getLatestEvidence(numericRunId, evidenceStepId);
        setLatestEvidence(refreshed || null);
      } catch {
        setLatestEvidence(null);
      }
      await continueToNextStudioStep(
        setEvidenceError,
        'Saved, but we could not open the next independent-work step. Use the Independent Work step buttons above to continue.',
      );
      if (shouldFinishStudioOnSubmit) {
        setPendingStudioFinishAnnouncement(true);
      }
      await reloadStudentState();
      requestPresentationRefresh();
    } catch (err: any) {
      if (handleGateError(err)) return;
      setEvidenceError(err?.response?.data?.error || inRunSubmissionCopy.submitError);
    } finally {
      setEvidenceSubmitting(false);
    }
  };

  const openExternalArtifactSubmission = async () => {
    if (!artifactStepId || !artifactExternalUrl) return;
    window.open(artifactExternalUrl, '_blank', 'noopener,noreferrer');
    if (isStudentPreview) {
      setArtifactNotice('Preview opened the external artifact link. No artifact status was changed.');
      return;
    }
    if (!numericRunId) return;
    setArtifactActionSubmitting('drafting');
    setArtifactError('');
    setArtifactNotice('');
    try {
      await lessonRunsApi.artifactMarkDrafting(numericRunId, artifactStepId, {
        metadata: {
          source: 'run_presentation_open_external',
        },
      });
      setArtifactNotice('Headrush opened. You can still submit your official artifact after class moves on.');
      await reloadStudentState();
    } catch (err: any) {
      if (handleGateError(err)) return;
      setArtifactError(err?.response?.data?.error || 'Could not update artifact status.');
    } finally {
      setArtifactActionSubmitting(null);
    }
  };

  const markExternalArtifactSubmitted = async () => {
    if (!artifactStepId) return;
    if (isStudentPreview) {
      setArtifactNotice('Preview marked this artifact locally. No student artifact status was changed.');
      setEvidenceSubmitted(true);
      return;
    }
    if (!numericRunId) return;
    setArtifactActionSubmitting('submitted');
    setArtifactError('');
    setArtifactNotice('');
    try {
      await lessonRunsApi.artifactMarkSubmitted(numericRunId, artifactStepId, {
        source_url: artifactExternalUrl || undefined,
        source_ref: artifactExternalAssignmentId || undefined,
        metadata: {
          source: 'run_presentation_manual_submit',
        },
      });
      setArtifactNotice('Marked as submitted in Headrush. Your teacher will review the official artifact.');
      await continueToNextStudioStep(
        setArtifactError,
        'Saved, but we could not open the next independent-work step. Use the Independent Work step buttons above to continue.',
      );
      if (shouldFinishStudioOnSubmit) {
        setPendingStudioFinishAnnouncement(true);
      }
      await reloadStudentState();
      requestPresentationRefresh();
    } catch (err: any) {
      if (handleGateError(err)) return;
      setArtifactError(err?.response?.data?.error || 'Could not mark the artifact as submitted.');
    } finally {
      setArtifactActionSubmitting(null);
    }
  };

  const navigateStudioStep = async (targetStepId: number, completeCurrent = false) => {
    if (!numericRunId || !isActualStudent || !isStudioStep) return false;
    if (!completeCurrent && targetStepId === step?.id && studentStepId === step?.id) return false;
    setStudioNavLoadingStepId(targetStepId);
    setSignalError('');
    try {
      await lessonRunsApi.navigateStudio(numericRunId, targetStepId);
      await reloadStudentState();
      requestPresentationRefresh();
      return true;
    } catch (err: any) {
      if (handleGateError(err)) return false;
      setSignalError(err?.response?.data?.error || 'Could not open that independent-work step.');
      return false;
    } finally {
      setStudioNavLoadingStepId(null);
    }
  };

  const submitStudioHelpRequest = async (resolve = false) => {
    if (!numericRunId || !step?.id || !isStudioStep) return;
    if (!resolve && !studioHelpKind) return;
    setStudioHelpSubmitting(true);
    setStudioHelpError('');
    setStudioHelpNotice('');
    try {
      await lessonRunsApi.requestStudioHelp(numericRunId, step.id, {
        help_kind: studioHelpKind,
        help_note: studioHelpNote,
        resolve,
      });
      await reloadStudentState();
      if (resolve) {
        setStudioHelpNotice('Marked as resolved. You can keep working independently.');
      } else {
        setStudioHelpNotice('Help requested. Your teacher can see this in the Studio board.');
      }
      setStudioHelpPanelOpen(false);
    } catch (err: any) {
      if (handleGateError(err)) return;
      setStudioHelpError(err?.response?.data?.error || 'Could not update your help request.');
    } finally {
      setStudioHelpSubmitting(false);
    }
  };

  const submitDraftCoach = async () => {
    if (!artifactStepId) return;
    if (!coachDraftText.trim() && !coachDraftLink.trim()) {
      setCoachError('Add a draft summary or a draft link before running the draft check.');
      return;
    }
    if (!isCoachLinkValid) {
      setCoachError('Enter a valid draft link starting with http:// or https://');
      return;
    }
    setCoachSubmitting(true);
    setCoachError('');
    setCoachNotice('');
    if (isStudentPreview) {
      setCoachPanelOpen(true);
      setCoachNotice('Preview draft check complete locally. No AI coach session was created.');
      setCoachSubmitting(false);
      return;
    }
    if (!numericRunId) return;
    try {
      const response = await lessonRunsApi.artifactCoach(numericRunId, artifactStepId, {
        draft_text: coachDraftText.trim() || undefined,
        draft_link: coachDraftLink.trim() || undefined,
      });
      const coachStatus = String(response?.latest_coach_session?.status || '').trim().toLowerCase();
      if (coachStatus === 'clear') {
        setCoachNotice('Draft check complete. This draft looks ready to submit.');
      } else if (coachStatus === 'flagged') {
        setCoachNotice('Draft check complete. Review the feedback below before you submit.');
      } else {
        setCoachNotice('Draft check finished. You can still submit your artifact if needed.');
      }
      setCoachPanelOpen(true);
      await reloadStudentState();
      requestPresentationRefresh();
    } catch (err: any) {
      if (handleGateError(err)) return;
      setCoachError(err?.response?.data?.error || 'Could not run the draft check.');
    } finally {
      setCoachSubmitting(false);
    }
  };

  const guidedRequiredComplete = useMemo(() => {
    if (!isGuidedSequenceSignal || guidedSequenceCards.length === 0) return false;
    return guidedSequenceCards.every((card) => {
      const candidate = guidedResponses[card.key] || '';
      if (!card.required) return true;
      return Boolean(String(candidate).trim());
    });
  }, [isGuidedSequenceSignal, guidedSequenceCards, guidedResponses]);
  const currentGuidedCardValue = currentGuidedCard ? (guidedResponses[currentGuidedCard.key] || '') : '';
  const currentGuidedCardError = currentGuidedCard
    ? getGuidedCardError(currentGuidedCard, currentGuidedCardValue)
    : '';
  const signalGateBlocked = (confidenceRequired && !confidenceSelected) || !hasValidReasoning;
  const evidenceGateBlocked = hasSignalConfig && reasoningRequired && !hasValidReasoning;
  const signalAlreadySubmitted = Boolean(myStepSignal || signalSubmitted);
  const interactionLocked = Boolean(signalAlreadySubmitted || signalSubmitting || evidenceSubmitting);
  const revealAnswer = (data as any)?.current_step_reveal_answer ?? myState?.current_step_reveal_answer ?? null;
  const signalOptions = Array.isArray((signalConfig as any)?.options) ? (signalConfig as any).options : [];
  const localSelectedIndex = useMemo(() => {
    const trimmedSignalValue = String(signalValue || '').trim();
    if (!trimmedSignalValue) return null;
    const matchedIndex = signalOptions.findIndex((option: any) => getSignalOptionText(option) === trimmedSignalValue);
    return matchedIndex >= 0 ? matchedIndex : null;
  }, [signalOptions, signalValue]);
  const serverSelectedIndexValue = Number(myStepSignal?.selected_index);
  const serverSelectedIndex = Number.isFinite(serverSelectedIndexValue) ? serverSelectedIndexValue : null;
  const selectedIndex = localSelectedIndex ?? serverSelectedIndex;
  const correctIndexValue = Number((data as any)?.current_step_correct_index ?? myState?.current_step_correct_index);
  const correctIndex = Number.isFinite(correctIndexValue) ? correctIndexValue : null;
  const correctIndices = Array.isArray(revealAnswer?.correct_indices)
    ? revealAnswer.correct_indices
      .map((idx: any) => Number(idx))
      .filter((idx: number) => Number.isInteger(idx) && idx >= 0)
    : [];
  const isRevealModeTeacher = revealMode === 'teacher';
  const shouldShowRevealResult = signalAlreadySubmitted && (!isRevealModeTeacher || currentStepRevealed);
  const teacherPresentationRevealActive = allowControlSummary && isRevealModeTeacher && currentStepRevealed;
  const shouldShowRevealPanels = shouldShowRevealResult || teacherPresentationRevealActive;
  const revealAnswerVisible = Boolean(revealAnswer) && shouldShowRevealPanels;
  const isDragDropReveal = revealAnswer?.kind === 'drag_drop';
  const orderingStudentResponseRaw = myStepSignalResponse?.ordering ?? myStepSignalResponse?.value;
  const orderingStudentResponse = Array.isArray(orderingStudentResponseRaw)
    ? orderingStudentResponseRaw.map((item: any) => String(item))
    : orderingItems;
  const orderingCheck = useMemo(() => {
    if (revealAnswer?.kind !== 'ordering') return { status: null as null | boolean, mismatchIndex: null as null | number };

    const correctRaw = Array.isArray((revealAnswer as any)?.answer) ? (revealAnswer as any).answer : [];
    const studentRaw = Array.isArray(orderingStudentResponse) ? orderingStudentResponse : [];

    const normalize = (arr: any[]) => arr.map((x) => String(x ?? '').trim());
    const correct = normalize(correctRaw);
    const student = normalize(studentRaw);

    if (correct.length === 0 || student.length === 0) return { status: null, mismatchIndex: null };
    if (correct.length !== student.length) return { status: false, mismatchIndex: Math.min(correct.length, student.length) + 1 };

    for (let i = 0; i < correct.length; i++) {
      if (correct[i] !== student[i]) {
        return { status: false, mismatchIndex: i + 1 };
      }
    }
    return { status: true, mismatchIndex: null };
  }, [revealAnswer, orderingStudentResponse]);
  const isDragDropRevealStep = isDragDropCodeSignal;
  // IMPORTANT: do not compute/read expected tokens until reveal gate is true (prevents answer-key leakage).
  const dragDropRevealGate = isDragDropRevealStep && (isRevealModeTeacher ? currentStepRevealed : shouldShowRevealResult);
  const revealAnswerTokens = (
    revealAnswer?.kind === 'drag_drop' && Array.isArray(revealAnswer?.answer_tokens)
  ) ? revealAnswer.answer_tokens : null;
  const rawExpectedTokens =
    dragDropRevealGate
      ? (
        Array.isArray(revealAnswerTokens)
          ? revealAnswerTokens
          : (Array.isArray((sourceItem as any)?.answer_tokens) ? (sourceItem as any).answer_tokens : [])
      )
      : [];
  // Slot count should be driven by the prompt/blanks, not by the key length (avoid phantom blanks).
  const dragDropSlotsTotal = dragDropBlankCount || dragDropPromptLines.length;
  const dragDropExpectedTokens = Array.from({ length: dragDropSlotsTotal }, (_, idx) => {
    const value = rawExpectedTokens[idx];
    if (value === null || value === undefined) return null;
    const token = String(value).trim();
    return token.length > 0 ? token : null;
  });
  const hasDragDropAnswerKey = dragDropRevealGate
    && dragDropBlankCount > 0
    && Array.from({ length: dragDropBlankCount }).every((_, idx) => Boolean(dragDropExpectedTokens[idx]));
  const dragDropPlacedTokensRaw = Array.isArray(myStepSignalResponse?.placed_tokens)
    ? myStepSignalResponse.placed_tokens
    : [];
  const hasStudentDragDropResponse = dragDropPlacedTokensRaw.some((value: any) => String(value ?? '').trim().length > 0);
  const dragDropPlacedTokens = Array.from({ length: dragDropSlotsTotal }, (_, idx) => {
    const value = dragDropPlacedTokensRaw[idx];
    if (value === null || value === undefined) return null;
    const token = String(value).trim();
    return token.length > 0 ? token : null;
  });
  const dragDropCorrectness = Array.from({ length: dragDropSlotsTotal }, (_, idx: number) => {
    if (!hasDragDropAnswerKey) return false;
    const placed = dragDropPlacedTokens[idx];
    const expected = dragDropExpectedTokens[idx];
    return Boolean(placed && expected && placed === expected);
  });
  const dragDropCorrectCount = dragDropCorrectness.filter(Boolean).length;
  const dragDropRevealResultsVisible = shouldShowRevealPanels && isDragDropRevealStep && (allowControlSummary || currentStepRevealed);
  const isCorrectSelection = shouldShowRevealResult && selectedIndex !== null && correctIndex !== null
    ? selectedIndex === correctIndex
    : false;
  const evidenceDisabled = !canSubmit || !isCurrentStep || evidenceGateBlocked || interactionLocked;
  const previousAnswersVisibleItems = useMemo(() => {
    const payload = previousAnswersData;
    const itemsRaw = payload && Array.isArray(payload.items) ? payload.items : [];
    return itemsRaw
      .map((item: any) => {
        const stepId = Number(item?.step_id);
        if (!Number.isInteger(stepId) || stepId <= 0) return null;
        const responseText = String(item?.response_text || '').trim();
        if (!responseText) return null;
        const label = String(item?.label || item?.step_title || `Step ${stepId}`).trim();
        const stepTitle = String(item?.step_title || '').trim();
        return {
          step_id: stepId,
          label,
          step_title: stepTitle,
          response_text: responseText,
        };
      })
      .filter((item: any): item is { step_id: number; label: string; step_title: string; response_text: string } => Boolean(item));
  }, [previousAnswersData]);
  const shouldShowPreviousAnswersHelper = Boolean(
    hasSignalConfig
    && previousAnswersData
    && String(previousAnswersData.mode || '').trim().toLowerCase() !== 'off'
    && previousAnswersVisibleItems.length > 0,
  );
  const previousAnswersTitle = String(previousAnswersData?.title || '').trim() || 'My previous answers';
  const canInsertIntoTextSignal = !isGuidedSequenceSignal
    && !isMcqSignal
    && !isOrderingSignal
    && !isMatchingSignal
    && !isDragDropCodeSignal;
  const canInsertIntoGuidedCard = isGuidedSequenceSignal && Boolean(currentGuidedCard?.key);
  const previousAnswersAllowInsert = Boolean(previousAnswersData?.allow_insert);
  const canInsertPreviousAnswers = Boolean(
    previousAnswersAllowInsert
    && !interactionLocked
    && (canInsertIntoTextSignal || canInsertIntoGuidedCard),
  );
  const insertPreviousAnswer = useCallback((responseText: string) => {
    const snippet = String(responseText || '').trim();
    if (!snippet) return;
    if (canInsertIntoGuidedCard && currentGuidedCard?.key) {
      setGuidedResponses((prev) => {
        const existing = String(prev[currentGuidedCard.key] || '').trim();
        return {
          ...prev,
          [currentGuidedCard.key]: existing ? `${existing}\n${snippet}` : snippet,
        };
      });
      return;
    }
    if (canInsertIntoTextSignal) {
      setSignalText((prev) => {
        const existing = String(prev || '').trim();
        return existing ? `${existing}\n${snippet}` : snippet;
      });
    }
  }, [canInsertIntoGuidedCard, canInsertIntoTextSignal, currentGuidedCard]);
  const canSubmitSignal = isGuidedSequenceSignal
    ? guidedRequiredComplete
    : (isMcqSignal
      ? (isMultiSelectSignal ? selectedSignalIndices.length === requiredSelections : Boolean(signalValue))
      : (isOrderingSignal
        ? orderingItems.length > 0
        : (isMatchingSignal
          ? (matchingLeftItems.length > 0 && matchingLeftItems.every((left: string) => Boolean(String(cleanedMatchingPairs[left] || '').trim())))
          : (isDragDropCodeSignal ? (dragDropBlankCount > 0 && dragDropSlots.every((slot) => Boolean(slot?.token && slot.token.trim().length > 0))) : Boolean(signalText.trim())))));
  const signalDisabled = !canSubmit || !isCurrentStep || signalGateBlocked || interactionLocked || !canSubmitSignal;
  const selectedOptionKey = useMemo(() => {
    if (isMultiSelectSignal && selectedSignalIndices.length > 0) {
      return selectedSignalIndices.join(',');
    }
    if (localSelectedIndex !== null) {
      return String(localSelectedIndex);
    }
    if (selectedIndex !== null) {
      return String(selectedIndex);
    }
    const trimmedSignalValue = String(signalValue || '').trim();
    if (trimmedSignalValue) {
      const valueIndex = signalOptions.findIndex((option: any) => getSignalOptionText(option) === trimmedSignalValue);
      return valueIndex >= 0 ? String(valueIndex) : trimmedSignalValue;
    }
    const rawResponseValue = (myStepSignalResponse as any)?.value;
    if (rawResponseValue !== undefined && rawResponseValue !== null) {
      return String(rawResponseValue);
    }
    return null;
  }, [isMultiSelectSignal, localSelectedIndex, selectedSignalIndices, selectedIndex, signalOptions, signalValue, myStepSignalResponse]);
  const mySelectedIndices = useMemo(() => {
    if (selectedSignalIndices.length > 0) {
      return selectedSignalIndices;
    }
    const selectedIndicesRaw = (myStepSignalResponse as any)?.selected_indices;
    if (Array.isArray(selectedIndicesRaw)) {
      return selectedIndicesRaw
        .map((value: any) => Number(value))
        .filter((value: number) => Number.isInteger(value) && value >= 0);
    }
    if (selectedIndex !== null) {
      return [selectedIndex];
    }
    return [];
  }, [myStepSignalResponse, selectedSignalIndices, selectedIndex]);
  const handleOverlayAssist = useCallback(async (button: OverlayButtonDef) => {
    if (!numericRunId || !step?.id) return;
    setOverlayAssistLoadingKey(button.key);
    setOverlayAssistError('');
    try {
      const response = await lessonRunsApi.aiMicroAssist(numericRunId, {
        action_type: button.action_type,
        context: {
          overlay_mode: overlayMode,
          overlay_step_id: step.id,
          selected_option_key: selectedOptionKey,
          my_step_signal: {
            selected_indices: mySelectedIndices,
          },
          current_step_revealed: currentStepRevealed,
          current_step_reveal_answer: revealAnswer,
          current_step: {
            id: step.id,
            title: step.title,
            stage_tag: step.stage_tag,
            step_kind: step.step_kind,
            content: step.content,
            signal_config: step.signal_config,
          },
        },
      });
      const payload = response?.data || {};
      const rawText = String(
        payload?.suggestion_text
        || payload?.step_by_step
        || payload?.result?.summary
        || payload?.summary
        || '',
      ).trim();
      const compactText = rawText.length > 320 ? `${rawText.slice(0, 317)}...` : rawText;
      if (!compactText) {
        setOverlayAssistError('AI returned an empty response.');
        return;
      }
      setOverlayAssistByKey((prev) => ({ ...prev, [button.key]: compactText }));
      setOverlayExpandedByKey((prev) => ({ ...prev, [button.key]: true }));
      if (button.key === 'quick_check') {
        setOverlayReplySavedByKey((prev) => ({ ...prev, [button.key]: false }));
        setOverlayReplyErrorByKey((prev) => ({ ...prev, [button.key]: '' }));
        setOverlayReplyTextByKey((prev) => ({ ...prev, [button.key]: prev[button.key] ?? '' }));
      }
    } catch (err: any) {
      if (handleGateError(err)) return;
      setOverlayAssistError(err?.response?.data?.error || 'Could not load scaffold.');
    } finally {
      setOverlayAssistLoadingKey(null);
    }
  }, [numericRunId, step, overlayMode, selectedOptionKey, mySelectedIndices, currentStepRevealed, revealAnswer, handleGateError]);
  const handleSubmitOverlayReply = useCallback(async (button: OverlayButtonDef) => {
    if (!numericRunId || !step?.id) return;
    const text = String(overlayReplyTextByKey[button.key] || '').trim();
    if (!text) {
      setOverlayReplyErrorByKey((prev) => ({ ...prev, [button.key]: 'Please type a short answer first.' }));
      return;
    }
    setOverlayReplySavingKey(button.key);
    setOverlayReplyErrorByKey((prev) => ({ ...prev, [button.key]: '' }));
    try {
      await lessonRunsApi.overlayReply(numericRunId, {
        step_id: step.id,
        text,
        overlay_mode: overlayMode,
        preset_key: button.key,
        action_type: button.action_type,
      });
      setOverlayReplySavedByKey((prev) => ({ ...prev, [button.key]: true }));
    } catch (err: any) {
      if (handleGateError(err)) return;
      setOverlayReplyErrorByKey((prev) => ({
        ...prev,
        [button.key]: err?.response?.data?.error || 'Could not submit your answer.',
      }));
    } finally {
      setOverlayReplySavingKey(null);
    }
  }, [numericRunId, step, overlayMode, overlayReplyTextByKey, handleGateError]);
  const latestEvidenceTimestamp = latestEvidence?.submitted_at
    ? new Date(latestEvidence.submitted_at).toLocaleString()
    : null;
  const artifactSubmittedTimestamp = currentArtifact?.submitted_at
    ? new Date(currentArtifact.submitted_at).toLocaleString()
    : null;
  const normalizeDraft = (value?: string | null) => (value || '').trim().replace(/\s+/g, ' ');
  const latestEvidenceRaw = latestEvidence?.content?.text ?? latestEvidence?.content?.url ?? '';
  const draftMatchesLatest = Boolean(
    latestEvidence && normalizeDraft(isEvidenceLink ? evidenceLink : evidenceText) === normalizeDraft(latestEvidenceRaw),
  );
  const placeDragDropToken = useCallback((targetIndex: number, item: DragTokenItem, fromSlotIndex: number | null) => {
    // Lock after teacher reveal to prevent post-reveal changes.
    if (dragDropRevealGate) return;
    setDragDropSlots((prev) => {
      const next = [...prev];
      const cleanToken = String(item?.token ?? '').trim();
      if (!cleanToken) return next;
      const isSlotMove = item?.source === 'slot';
      const sourceSlotIndex = (
        isSlotMove
        && (
          (typeof item?.fromSlotIndex === 'number' && item.fromSlotIndex >= 0)
          || (typeof fromSlotIndex === 'number' && fromSlotIndex >= 0)
        )
      )
        ? (typeof item?.fromSlotIndex === 'number' && item.fromSlotIndex >= 0 ? item.fromSlotIndex : fromSlotIndex)
        : null;
      const nextInstanceId = isSlotMove
        ? (typeof item?.instanceId === 'number' && item.instanceId > 0
          ? item.instanceId
          : dragDropInstanceIdRef.current++)
        : dragDropInstanceIdRef.current++;
      const targetPrev = next[targetIndex] ?? null;

      // Guarantee uniqueness: an instanceId can only exist in one slot.
      for (let i = 0; i < next.length; i += 1) {
        if (next[i]?.instanceId === nextInstanceId) {
          next[i] = null;
        }
      }

      // Move semantics for slot drags: swap with target.
      if (sourceSlotIndex !== null && sourceSlotIndex < next.length) {
        next[sourceSlotIndex] = targetPrev;
      }

      next[targetIndex] = { instanceId: nextInstanceId, token: cleanToken };
      return next;
    });
  }, [dragDropRevealGate]);
  const handleDropDragDropItem = useCallback((targetIndex: number, item: DragTokenItem) => {
    const fromSlotIndexRaw = item?.fromSlotIndex;
    const fromSlotIndex = typeof fromSlotIndexRaw === 'number' && Number.isInteger(fromSlotIndexRaw)
      ? fromSlotIndexRaw
      : null;
    placeDragDropToken(targetIndex, item, fromSlotIndex);
  }, [placeDragDropToken]);

  const presentationContent = (
    <div className={redesignEnabled ? 'bg-gray-50' : 'min-h-screen bg-gray-50'}>
      {!redesignEnabled && showBackToControl && (
        <a
          href={teacherRunPath}
          className="fixed top-4 right-4 z-20 text-xs px-3 py-1 rounded border border-gray-300 bg-white text-gray-600 hover:text-gray-800 hover:border-gray-400"
        >
          ← Back to Control
        </a>
      )}
      {!redesignEnabled && showBackToDashboard && (
        <Link
          to="/v2/student"
          className="fixed top-4 right-4 z-20 text-xs px-3 py-1 rounded border border-gray-300 bg-white text-gray-600 hover:text-gray-800 hover:border-gray-400"
        >
          ← Back to Dashboard
        </Link>
      )}
      <header className={redesignEnabled ? 'bg-white/90 backdrop-blur border-b border-gray-200' : 'sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-gray-200'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Lesson Run</p>
            <p className="text-lg font-semibold text-gray-900">{isStudentPreview ? 'Student Preview' : 'Presentation'}</p>
          </div>
          <div className="text-right">
            {updating && <div className="text-xs text-gray-500">Updating…</div>}
            {relativeUpdated && (
              <div className="text-[11px] text-gray-400">{relativeUpdated}</div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {isActualStudent && isStudioStep ? <section aria-label="Draft recovery" className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
          <p role="status" className="font-medium text-slate-900">{studioDraft.status}</p>
          {studioDraft.error ? <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950">{studioDraft.error}</p> : null}
          <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={studioDraft.retry}>Save / retry draft</button>
          {studioDraft.conflict ? <div className="space-y-3">
            <p className="text-sm text-slate-600">Copy any text you need before refreshing or leaving. These recovery copies are not submitted evidence.</p>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block text-sm font-medium text-slate-800">Your recovery copy<textarea rows={5} className="mt-2 block w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" readOnly value={formatStudioRecovery(studioDraft.recovery)} /></label>
              <label className="block text-sm font-medium text-slate-800">Comparison copy<textarea rows={5} className="mt-2 block w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-normal focus:outline-none focus:ring-2 focus:ring-blue-500" readOnly value={formatStudioRecovery(studioDraft.conflict)} /></label>
            </div>
            <button type="button" className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2" onClick={() => void studioDraft.useServerCopy()}>Keep recovery copy and use server version</button>
          </div> : null}
          {studioDraft.backup ? <details className="text-sm"><summary className="cursor-pointer font-medium">Previous recovery copy</summary>
            <textarea rows={5} className="mt-2 block w-full rounded-lg border border-slate-300 bg-slate-50 p-3" aria-label="Previous recovery copy" readOnly value={formatStudioRecovery(studioDraft.backup)} />
          </details> : null}
        </section> : null}
        <fieldset disabled={isActualStudent && isStudioStep && !studioDraft.ready} className="min-w-0 space-y-6">
        {studentPresentation ? (
          <section aria-label="Teacher presentation" className="overflow-hidden rounded-2xl border border-violet-200 bg-slate-950 shadow-lg">
            <img
              src={studentPresentation.thumbnail_url}
              alt={`${studentPresentation.title}, slide ${studentPresentation.slide_number}`}
              className="mx-auto max-h-[70vh] min-h-80 w-full object-contain"
            />
            <div className="flex items-center justify-between gap-3 bg-white px-4 py-3 text-sm">
              <span className="font-semibold text-slate-900">{studentPresentation.title}</span>
              <span className="text-slate-600">Slide {studentPresentation.slide_index + 1} of {studentPresentation.slide_count} · controlled by your teacher</span>
            </div>
          </section>
        ) : null}
        {isStudentPreview && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            {isPlanPreview && numericPlanId ? <div className="mb-3"><PreviewUnitReturnLink planId={numericPlanId} /></div> : null}
            <p className="font-semibold">{isPlanPreview ? 'Pre-run student preview' : 'Student preview mode'}</p>
            <p className="mt-1">
              You can answer the live response as a student would. Submissions on this page stay local to your browser and do not create a run, change student work, update reports, or affect live dashboards.
            </p>
            {isPlanPreview && allLessonSteps.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {allLessonSteps.map((candidate: any) => {
                  const candidateId = Number(candidate?.id);
                  if (!Number.isInteger(candidateId)) return null;
                  const isActivePreviewStep = candidateId === step?.id;
                  return (
                    <button
                      key={`plan-preview-step-${candidateId}`}
                      type="button"
                      className={`rounded-full px-3 py-1.5 text-xs ${
                        isActivePreviewStep
                          ? 'bg-sky-900 text-white'
                          : 'border border-sky-200 bg-white text-sky-900 hover:bg-sky-100'
                      }`}
                      onClick={() => setPlanPreviewStepId(candidateId)}
                    >
                      {candidate?.title || `Step ${candidate?.order || ''}`.trim() || `Step ${candidateId}`}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {user?.role === 'student' && <UnitLiveReturnLink />}
        {runStatus === 'completed' && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
            Lesson ended by the teacher. <Link className="underline font-semibold" to="/v2/student/units">Continue at My next step</Link> or return to your dashboard.
          </div>
        )}

        {runStatus === 'paused' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
            Waiting for teacher… this run is paused. Stay on this page for updates.
          </div>
        )}

        {readinessRouteMessage && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800">
            <span className="font-medium">{readinessRouteLabel || 'Readiness route'}</span>
            <span className="ml-2">{readinessRouteMessage}</span>
          </div>
        )}

        {deliveryPhase === 'studio' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Independent Work</p>
                <p className="mt-1 text-sm font-medium text-amber-950">
                  {runStatus === 'completed'
                    ? 'This lesson has ended. Saved work and recovery copies remain available.'
                    : runStatus === 'paused'
                      ? 'Independent work is paused. Wait for your teacher to resume.'
                      : 'Teacher-paced lesson is complete. Continue the rest independently.'}
                </p>
                {studioCompletion?.display_label ? (
                  <p className="mt-2 text-sm text-amber-900">{studioCompletion.display_label}</p>
                ) : null}
                {currentStepIsRouteOnlyPathway ? (
                  <p className="mt-2 text-sm text-amber-900">
                    This is a pathway step from your teacher. When you finish, return to your Independent Work steps below.
                  </p>
                ) : null}
              </div>
              <div className="text-sm text-amber-900">
                {currentStudioProgress?.status_label || 'In progress'}
              </div>
            </div>
            {studioStepIds.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {studioStepIds.map((studioStepId: number, index: number) => {
                  const isActiveStudioStep = studioStepId === step?.id;
                  return (
                    <button
                      key={`studio-nav-${studioStepId}`}
                      type="button"
                      className={`rounded-full px-3 py-1.5 text-xs ${
                        isActiveStudioStep
                          ? 'bg-amber-900 text-white'
                          : 'border border-amber-300 bg-white text-amber-900 hover:bg-amber-100'
                      }`}
                      onClick={() => {
                        void navigateStudioStep(studioStepId);
                      }}
                      disabled={studioNavLoadingStepId !== null}
                    >
                      {studioNavLoadingStepId === studioStepId
                        ? 'Opening…'
                        : (studioStepLabelById[studioStepId] || `Independent step ${index + 1}`)}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        )}

        {showStudioFinishedPanel && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-emerald-700">Independent Work Finished</p>
                <p className="mt-1 text-base font-semibold text-emerald-950">
                  You&apos;re finished with today&apos;s independent work.
                </p>
                <p className="mt-2 text-sm text-emerald-900">
                  Your submitted work stays on this page below if you want to review it now.
                </p>
              </div>
              <Link
                to="/v2/student"
                className="inline-flex items-center justify-center rounded border border-emerald-300 bg-white px-4 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
              >
                Return to dashboard
              </Link>
            </div>
          </div>
        )}

        {canFinishInformationalStudioStep && step?.id ? (
          <div className="rounded-lg border border-amber-200 bg-white p-4">
            <p className="text-sm text-slate-700">
              This final step does not require a submission. Mark it finished when you are done reviewing it.
            </p>
            <button
              type="button"
              className="mt-3 rounded bg-amber-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
              disabled={studioNavLoadingStepId !== null}
              onClick={async () => {
                const finished = await navigateStudioStep(step.id, true);
                if (finished) setPendingStudioFinishAnnouncement(true);
              }}
            >
              {studioNavLoadingStepId === step.id ? 'Finishing…' : 'Finish independent work'}
            </button>
          </div>
        ) : null}

        {step && (
          <ResourceToolkit
            resources={availableResources}
            lifecycleKey={mediaLifecycleKey}
            sessionKey={`${numericRunId ?? 'preview'}:${step.id}`}
            preserveVideoPosition={isStudioStep}
            onResourceOpen={(resource) => {
              if (numericRunId && !isPlanPreview) {
                void lessonRunsApi.resourceOpen(numericRunId, { title: resource.title, src: resource.src });
              }
            }}
          />
        )}

        {!showStudioFinishedPanel && isActualStudent && deliveryPhase === 'studio' && isLastStudioStep && studioArtifactStillNeeded && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Independent work is nearly complete. Submit the final artifact on this last step to finish.
          </div>
        )}

        {showExplainBroadcast && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">Teacher Explanation</p>
              <button
                type="button"
                className="text-xs text-indigo-700 hover:text-indigo-900"
                onClick={() => setDismissedBroadcastKeys((prev) => ({ ...prev, [broadcastKey]: true }))}
              >
                Got it
              </button>
            </div>
            <div className="mt-3">
              <ExplainBroadcastContent markdown={String(broadcast?.content_md || '')} />
            </div>
          </div>
        )}

        {showOverlayCard && (
          <section className="rounded-lg border border-teal-200 bg-teal-50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.2em] text-teal-700">
                Scaffold Overlay
              </p>
              <span className="text-xs text-teal-800">
                {overlayMode === 'repair' ? 'Repair mode' : 'Extend mode'}
              </span>
            </div>
            {overlayMode === 'repair' && overlayRepairCard ? (
              <div className="rounded border border-teal-200 bg-white p-3 space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-teal-700">Repair Card</p>
                {overlayRepairCard.vocabulary.length > 0 ? (
                  <div>
                    <p className="text-[11px] font-semibold text-teal-900">Vocabulary</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {overlayRepairCard.vocabulary.map((term, idx) => (
                        <span
                          key={`repair-vocab-${idx}-${term}`}
                          className="rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] text-teal-900"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {overlayRepairCard.common_trap ? (
                  <div>
                    <p className="text-[11px] font-semibold text-teal-900">Common trap</p>
                    <p className="text-xs text-teal-900">{overlayRepairCard.common_trap}</p>
                  </div>
                ) : null}
                {overlayRepairCard.self_check ? (
                  <div>
                    <p className="text-[11px] font-semibold text-teal-900">Self-check</p>
                    <p className="text-xs text-teal-900">{overlayRepairCard.self_check}</p>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {overlayButtons.map((button: OverlayButtonDef) => (
                <button
                  key={button.key}
                  type="button"
                  className="rounded border border-teal-300 bg-white px-3 py-1.5 text-xs text-teal-900 hover:bg-teal-100 disabled:opacity-50"
                  onClick={() => handleOverlayAssist(button)}
                  disabled={overlayAssistLoadingKey !== null}
                >
                  {overlayAssistLoadingKey === button.key ? 'Loading…' : button.label}
                </button>
              ))}
            </div>
            {overlayAssistError ? (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {overlayAssistError}
              </div>
            ) : null}
            {overlayButtons.map((button: OverlayButtonDef) => {
              const resultText = overlayAssistByKey[button.key];
              if (!resultText) return null;
              const expanded = Boolean(overlayExpandedByKey[button.key]);
              const isQuickCheck = button.key === 'quick_check';
              const replyText = overlayReplyTextByKey[button.key] || '';
              const replyError = overlayReplyErrorByKey[button.key] || '';
              const replySaved = Boolean(overlayReplySavedByKey[button.key]);
              const replySaving = overlayReplySavingKey === button.key;
              return (
                <details
                  key={`${button.key}-result`}
                  open={expanded}
                  onToggle={(event) => {
                    const nextOpen = (event.currentTarget as HTMLDetailsElement).open;
                    setOverlayExpandedByKey((prev) => ({ ...prev, [button.key]: nextOpen }));
                  }}
                  className="rounded border border-teal-200 bg-white p-3"
                >
                  <summary className="cursor-pointer text-xs font-semibold text-teal-900">
                    {button.label} response
                  </summary>
                  <div className="mt-2 text-sm text-teal-950">
                    <ReactMarkdown components={markdownComponents}>
                      {resultText}
                    </ReactMarkdown>
                  </div>
                  {isQuickCheck ? (
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-1 text-teal-900" htmlFor={`overlay-reply-${button.key}`}>
                        Your answer
                      </label>
                      <textarea
                        id={`overlay-reply-${button.key}`}
                        className="w-full border border-teal-200 rounded p-2 text-sm text-teal-950"
                        rows={3}
                        value={replyText}
                        onChange={(event) => {
                          const nextText = event.target.value;
                          setOverlayReplyTextByKey((prev) => ({ ...prev, [button.key]: nextText }));
                          setOverlayReplySavedByKey((prev) => ({ ...prev, [button.key]: false }));
                          if (overlayReplyErrorByKey[button.key]) {
                            setOverlayReplyErrorByKey((prev) => ({ ...prev, [button.key]: '' }));
                          }
                        }}
                        disabled={replySaving}
                        placeholder="Type your quick check answer here (1-2 sentences)."
                      />
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          className="rounded border border-teal-300 bg-white px-3 py-1.5 text-xs text-teal-900 hover:bg-teal-100 disabled:opacity-50"
                          onClick={() => handleSubmitOverlayReply(button)}
                          disabled={replySaving}
                        >
                          {replySaving ? 'Submitting...' : (replySaved ? 'Saved ✅' : 'Submit')}
                        </button>
                        {replyError ? (
                          <span className="text-xs text-red-700">{replyError}</span>
                        ) : null}
                      </div>
                      {replySaved && !replyError ? (
                        <p className="mt-2 text-xs text-teal-800">
                          Nice - now re-check one of your choices using order of operations.
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                </details>
              );
            })}
          </section>
        )}

        {loading && !step && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
            Loading current step…
          </div>
        )}

        {step && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            {isStudioStep && (
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
                  Independent work
                </div>
                {studioCompletion?.display_label ? (
                  <div className="text-xs text-slate-500">{studioCompletion.display_label}</div>
                ) : null}
              </div>
            )}
            {!isStudioStep && numericRunId && !isPlanPreview ? <LiveQuestionPanel runId={numericRunId} /> : null}
            {!isStudioStep && isClassCurrentStep ? (
              <div className="mb-4">
                <PacingTimerCard
                  content={step.content}
                  startedAt={currentStepStartedAt}
                  title={step.title || `Step ${step.order || ''}`.trim()}
                  runtimeState={currentStepTimer}
                />
              </div>
            ) : null}
            <StepRenderer
              title={step.title}
              stageTag={step.stage_tag}
              stepKind={step.step_kind}
              content={stepContentForRenderer}
              titleClassName={stepTitleClassName}
              mediaSessionKey={`${numericRunId ?? 'preview'}:${step.id}`}
              preserveVideoPosition={isStudioStep}
            />
            {isSimulationActivity && isActualStudent && numericRunId && simulationPayload ? (
              <SimulationActivity
                key={`${numericRunId}:${step.id}:${simulationPayload.case_id}`}
                runId={numericRunId}
                stepId={step.id}
                simulation={simulationPayload}
              />
            ) : null}
            {isSimulationActivity && isActualStudent && !simulationPayload ? (
              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                Loading simulation…
              </div>
            ) : null}
            {isSimulationActivity && isStudentPreview && simulationPreviewSession ? (
              <>
                <div className="mt-5 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
                  Preview sandbox — actions here are not saved to student progress or reports.
                </div>
                <SimulationActivity
                  key={`preview:${simulationPreviewKey}`}
                  runId={numericRunId || 0}
                  stepId={step.id}
                  simulation={simulationPreviewSession.state}
                  submitActionOverride={(actionId, expectedRevision) => (
                    simulationPreviewSession!.submit(actionId, expectedRevision)
                  )}
                />
              </>
            ) : null}
            {isSimulationActivity && isStudentPreview && !simulationPreviewSession ? (
              <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                This simulation cannot be previewed because its rules are unavailable.
              </div>
            ) : null}
            {isActualStudent && !isSimulationActivity && (
              <div className="mt-5 space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step Help</p>
                <HelpTab
                  runId={numericRunId ?? null}
                  currentStep={step}
                  runStatus={runStatus}
                  stepKey={`${numericRunId ?? 'none'}:${step?.id ?? 'none'}:${showFullStepUdl ? 'full' : showLimitedStepUdl ? 'limited' : 'fallback'}`}
                  supportMode={showFullStepUdl ? 'full' : showLimitedStepUdl ? 'limited' : 'fallback'}
                  limitedMessage={stepUdlLimitedMessage}
                />
              </div>
            )}
          </div>
        )}

        {!step && !loading && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-gray-600">
            Waiting for the teacher to start the next step. Keep this page open.
          </div>
        )}

        {isActualStudent && gateBlocksProgress && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Revision needed before reflection</p>
            <p className="text-sm text-amber-900">
              Your teacher or AI flagged this submission for revision. Update your response using the checklist below and resubmit.
            </p>
            {currentStepGateReasons.length > 0 && (
              <p className="text-xs text-amber-800">
                Reasons: {currentStepGateReasons.join(', ')}
              </p>
            )}
            {currentStepGateSuggestions.length > 0 ? (
              <ul className="list-disc pl-5 text-sm text-amber-900">
                {currentStepGateSuggestions.slice(0, 3).map((suggestion: string, idx: number) => (
                  <li key={`gate-suggestion-${idx}`}>{suggestion}</li>
                ))}
              </ul>
            ) : null}
          </section>
        )}

        {isActualStudent && gateAllowsProgress && (
          <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-700">Reflection unlocked</p>
            <p className="text-sm text-emerald-900">
              You are cleared to continue to reflection. If you are waiting for the class view to update, complete one extension:
              explain one trade-off in your approach and how you would improve your evidence.
            </p>
          </section>
        )}

        {isStudentSurface && qualityNotice ? (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-700">Submission quality</p>
            <p className="mt-1 text-sm text-blue-900">{qualityNotice}</p>
          </section>
        ) : null}

        {isActualStudent && (qualityNeedsFix || qualityResolved || fixQueueUnresolved > 0) ? (
          <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-2">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-700">Fix queue</p>
            <p className="text-sm text-slate-900">
              {fixQueueUnresolved > 0
                ? `You have ${fixQueueUnresolved} item${fixQueueUnresolved === 1 ? '' : 's'} to revise after live instruction.`
                : 'No unresolved fix queue items right now.'}
            </p>
            {qualityNeedsFix ? (
              <>
                {currentStepQualityReasons.length > 0 ? (
                  <p className="text-xs text-slate-700">Current step reasons: {currentStepQualityReasons.join(', ')}</p>
                ) : null}
                {currentStepQualitySuggestions.length > 0 ? (
                  <ul className="list-disc pl-5 text-sm text-slate-800">
                    {currentStepQualitySuggestions.slice(0, 2).map((suggestion: string, idx: number) => (
                      <li key={`quality-suggestion-${idx}`}>{suggestion}</li>
                    ))}
                  </ul>
                ) : null}
              </>
            ) : null}
            {qualityResolved ? (
              <p className="text-sm text-emerald-700">Your latest revision on this step is marked resolved.</p>
            ) : null}
          </section>
        ) : null}

        {hasSignalConfig && canSubmit && (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 p-5 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700">{liveResponseHeading}</p>
              <p className="mt-2 text-sm text-blue-900">This helps your teacher see what to do next.</p>
              <p className="text-xs text-blue-700">Take 1-2 minutes.</p>
              <div className="mt-2 text-sm text-blue-800">
                <ReactMarkdown components={markdownComponents}>
                  {signalPrompt || 'Share a quick response for your teacher.'}
                </ReactMarkdown>
              </div>
            </div>

            {signalError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {signalError}
              </div>
            )}

            <div className="space-y-3">
              {(previousAnswersLoading || previousAnswersError || shouldShowPreviousAnswersHelper) && (
                <section className="rounded-xl border border-blue-200 bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-blue-700">{previousAnswersTitle}</p>
                    {shouldShowPreviousAnswersHelper && (
                      <button
                        type="button"
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                        onClick={() => setPreviousAnswersOpen((prev) => !prev)}
                      >
                        {previousAnswersOpen ? 'Hide previous answers' : 'Show my previous answers'}
                      </button>
                    )}
                  </div>
                  {previousAnswersLoading && (
                    <p className="text-xs text-gray-600">Loading previous answers...</p>
                  )}
                  {!previousAnswersLoading && previousAnswersError && (
                    <p className="text-xs text-red-700">{previousAnswersError}</p>
                  )}
                  {!previousAnswersLoading && shouldShowPreviousAnswersHelper && previousAnswersOpen && (
                    <div className="space-y-2">
                      {previousAnswersVisibleItems.map((item) => (
                        <div key={`previous-answer-${item.step_id}`} className="rounded border border-blue-100 bg-blue-50 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-blue-900">{item.label}</p>
                            {canInsertPreviousAnswers && (
                              <button
                                type="button"
                                className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                                onClick={() => insertPreviousAnswer(item.response_text)}
                              >
                                Insert
                              </button>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-gray-800 whitespace-pre-wrap">{item.response_text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
              {isGuidedSequenceSignal && (
                <section className="rounded-xl border border-blue-200 bg-white p-4 space-y-3">
                  {guidedSequenceCards.length === 0 && (
                    <p className="text-sm text-amber-700">
                      No guided sequence cards are configured for this step.
                    </p>
                  )}
                  {currentGuidedCard && (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs uppercase tracking-[0.2em] text-blue-700">
                          Step {guidedCardIndex + 1} of {guidedSequenceCards.length}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{currentGuidedCard.prompt}</p>
                        {currentGuidedCard.required ? (
                          <p className="mt-1 text-xs text-gray-500">Required</p>
                        ) : null}
                      </div>
                      {currentGuidedCard.template ? (
                        <pre className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900 overflow-x-auto whitespace-pre-wrap">
                          <code>{currentGuidedCard.template}</code>
                        </pre>
                      ) : null}
                      {currentGuidedCard.input_type === 'select' ? (
                        <fieldset className="space-y-2">
                          <legend className="sr-only">{currentGuidedCard.prompt}</legend>
                          {currentGuidedCard.options.map((option) => (
                            <label key={option.value} className="flex cursor-pointer items-start gap-3 rounded border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-950">
                              <input
                                type="radio"
                                name={`guided-${currentGuidedCard.key}`}
                                value={option.value}
                                checked={currentGuidedCardValue === option.value}
                                onChange={(e) => setGuidedResponses((prev) => ({ ...prev, [currentGuidedCard.key]: e.target.value }))}
                                disabled={interactionLocked}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </fieldset>
                      ) : currentGuidedCard.input_type === 'textarea' ? (
                        <textarea
                          className="w-full border border-blue-200 rounded px-3 py-2 text-sm"
                          rows={4}
                          value={currentGuidedCardValue}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setGuidedResponses((prev) => ({ ...prev, [currentGuidedCard.key]: nextValue }));
                          }}
                          placeholder={currentGuidedCard.placeholder || 'Type your response'}
                          disabled={interactionLocked}
                        />
                      ) : (
                        <input
                          type="text"
                          className="w-full border border-blue-200 rounded px-3 py-2 text-sm"
                          value={currentGuidedCardValue}
                          onChange={(e) => {
                            const nextValue = e.target.value;
                            setGuidedResponses((prev) => ({ ...prev, [currentGuidedCard.key]: nextValue }));
                          }}
                          placeholder={currentGuidedCard.placeholder || 'Type your response'}
                          disabled={interactionLocked}
                        />
                      )}
                      {currentGuidedCardError ? (
                        <p className="text-xs text-red-700">{currentGuidedCardError}</p>
                      ) : null}
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded border border-blue-200 bg-white text-blue-800 text-sm disabled:opacity-50"
                          onClick={() => setGuidedCardIndex((prev) => Math.max(0, prev - 1))}
                          disabled={interactionLocked || guidedCardIndex === 0}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded border border-blue-200 bg-white text-blue-800 text-sm disabled:opacity-50"
                          onClick={() => setGuidedCardIndex((prev) => Math.min(guidedSequenceCards.length - 1, prev + 1))}
                          disabled={interactionLocked || guidedCardIndex >= guidedSequenceCards.length - 1}
                        >
                          Next
                        </button>
                      </div>
                    </>
                  )}
                </section>
              )}
              {!isGuidedSequenceSignal && isMcqSignal && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {(signalConfig?.options || []).map((option: any, index: number) => {
                    const optionText = getSignalOptionText(option);
                    const isSelected = isMultiSelectSignal
                      ? selectedSignalIndices.includes(index)
                      : (localSelectedIndex !== null ? localSelectedIndex === index : (selectedIndex !== null ? selectedIndex === index : signalValue === optionText));
                    const isCorrect = shouldShowRevealResult && (
                      isMultiSelectSignal ? correctIndices.includes(index) : correctIndex === index
                    );
                    const isIncorrectSelection = shouldShowRevealResult && isSelected && !isCorrect && (
                      isMultiSelectSignal ? correctIndices.length > 0 : correctIndex !== null
                    );
                    const baseClasses = 'px-3 py-2 rounded border text-sm flex items-center justify-between';
                    const stateClasses = isCorrect
                      ? 'border-green-500 bg-green-50 text-green-800'
                      : isIncorrectSelection
                        ? 'border-red-500 bg-red-50 text-red-800'
                        : isSelected
                          ? 'border-blue-500 bg-white text-blue-700'
                          : 'border-blue-200 bg-white text-blue-800';
                    if (isMultiSelectSignal) {
                      return (
                        <label
                          key={`${index}-${optionText}`}
                          className={`${baseClasses} ${stateClasses} ${interactionLocked || signalAlreadySubmitted ? 'opacity-70' : 'cursor-pointer'}`}
                        >
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedSignalIndices((prev) => {
                                  if (prev.includes(index)) return prev.filter((item) => item !== index);
                                  return [...prev, index].sort((a, b) => a - b);
                                });
                              }}
                              disabled={interactionLocked || signalAlreadySubmitted}
                            />
                            <span>{optionText}</span>
                          </span>
                          {shouldShowRevealResult && (
                            <span className="text-xs font-semibold">
                              {isCorrect && isSelected
                                ? 'Your choice • Correct'
                                : isCorrect
                                  ? 'Correct'
                                  : isIncorrectSelection || isSelected
                                    ? 'Your choice'
                                    : ''}
                            </span>
                          )}
                        </label>
                      );
                    }
                    return (
                      <button
                        key={`${index}-${optionText}`}
                        type="button"
                        onClick={() => {
                          setSignalValue(optionText);
                        }}
                        className={`${baseClasses} ${stateClasses}`}
                        disabled={interactionLocked || signalAlreadySubmitted}
                      >
                        <span>{optionText}</span>
                        {shouldShowRevealResult && (
                          <span className="text-xs font-semibold">
                            {isCorrect && isSelected
                              ? 'Your choice • Correct'
                              : isCorrect
                                ? 'Correct'
                                : isIncorrectSelection || isSelected
                                  ? 'Your choice'
                                  : ''}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {!isGuidedSequenceSignal && isMcqSignal && isMultiSelectSignal && !signalAlreadySubmitted && (
                <p className="text-xs text-gray-500">
                  Select {requiredSelections} option{requiredSelections === 1 ? '' : 's'}.
                </p>
              )}
              {!isGuidedSequenceSignal && !isMcqSignal && isOrderingSignal && (
                <OrderingResponseEditor items={orderingItems} disabled={interactionLocked} onChange={(items) => { orderingDirtyRef.current = true; setOrderingItems(items); }} />
              )}
              {!isGuidedSequenceSignal && !isMcqSignal && !isOrderingSignal && isMatchingSignal && (
                <MatchingResponseEditor leftItems={matchingLeftItems} rightItems={matchingRightItems} value={matchingPairs} seed={String(step?.id || 0)} disabled={interactionLocked} onChange={setMatchingPairs} />
              )}
              {!isGuidedSequenceSignal && !isMcqSignal && !isOrderingSignal && !isMatchingSignal && isDragDropCodeSignal && (
                <DragDropPrompt
                  mode={dragDropMode}
                  promptLines={dragDropPromptLines}
                  blankCount={dragDropBlankCount}
                  slots={dragDropSlots}
                  bankTokens={dragDropTokens}
                  interactionLocked={interactionLocked}
                  revealLocked={dragDropRevealGate}
                  revealGate={dragDropRevealGate}
                  expectedTokens={dragDropRevealGate ? dragDropExpectedTokens : undefined}
                  onDropItem={handleDropDragDropItem}
                  onClear={(slotIndex) => {
                    if (dragDropRevealGate) return;
                    setDragDropSlots((prev) => prev.map((value, mappedIdx) => (mappedIdx === slotIndex ? null : value)));
                  }}
                  onClickBankToken={(token) => {
                    if (dragDropRevealGate) return;
                    const nextSlot = dragDropSlots.findIndex((value) => value === null);
                    if (nextSlot < 0) return;
                    placeDragDropToken(nextSlot, { source: 'bank', token }, null);
                  }}
                />
              )}
              {!isGuidedSequenceSignal && dragDropRevealResultsVisible && (
                <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-700">Reveal results</p>
                  {hasDragDropAnswerKey ? (
                    <>
                      {hasStudentDragDropResponse ? (
                        <p className="text-sm text-emerald-900">{dragDropCorrectCount} / {dragDropSlotsTotal} correct</p>
                      ) : (
                        <p className="text-sm text-emerald-900">No student response yet.</p>
                      )}
                      {Array.from({ length: dragDropSlotsTotal }).map((_, idx: number) => {
                        const line = dragDropPromptLines[idx] || `Blank ${idx + 1}`;
                        const placed = dragDropPlacedTokens[idx];
                        const expected = dragDropExpectedTokens[idx];
                        const isCorrect = dragDropCorrectness[idx];
                        return (
                          <div key={`drag-result-${idx}`} className="rounded border border-emerald-200 bg-white px-3 py-2 text-sm space-y-1">
                            <div className="font-mono text-xs text-gray-700 whitespace-pre-wrap">{line}</div>
                            {hasStudentDragDropResponse ? (
                              <>
                                <div className="text-gray-800">Student: <span className="font-semibold">{placed || '(blank)'}</span></div>
                                {!isCorrect && (
                                  <div className="text-gray-800">Expected: <span className="font-semibold">{expected || '(blank)'}</span></div>
                                )}
                                <div className={isCorrect ? 'text-green-700' : 'text-red-700'}>{isCorrect ? '✅' : '❌'}</div>
                              </>
                            ) : (
                              <div className="text-gray-800">Expected: <span className="font-semibold">{expected || '(blank)'}</span></div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : isDragDropReveal ? (
                    <p className="text-sm text-amber-700">No answer key configured for this drag/drop step.</p>
                  ) : null}
                </section>
              )}
              {!isGuidedSequenceSignal && !isMcqSignal && !isOrderingSignal && !isMatchingSignal && !isDragDropCodeSignal && (
                  <textarea
                    className="w-full border border-blue-200 rounded px-3 py-2 text-sm"
                    rows={3}
                    value={signalText}
                    onChange={(e) => setSignalText(e.target.value)}
                    placeholder="Type your response"
                    disabled={interactionLocked}
                  />
              )}
              {!isGuidedSequenceSignal && signalAlreadySubmitted ? (
                isRevealModeTeacher ? (
                  <div className={`text-sm border rounded p-2 ${currentStepRevealed ? 'text-green-700 bg-green-50 border-green-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
                    {currentStepRevealed
                      ? (isMultiSelectSignal
                        ? 'Revealed. Review the correct selections below.'
                        : isCorrectSelection
                        ? 'Correct.'
                        : (selectedIndex !== null && correctIndex !== null && (signalConfig?.options || [])[selectedIndex] !== undefined
                          ? `You chose ${(signalConfig?.options || [])[selectedIndex]}. Correct is ${(signalConfig?.options || [])[correctIndex]}.`
                          : 'Revealed. Review the correct answer with your teacher.'))
                      : 'Response recorded. Waiting for reveal.'}
                  </div>
                ) : (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
                    {shouldShowRevealResult && correctIndex !== null && selectedIndex !== null
                      ? (isCorrectSelection
                        ? 'Correct.'
                        : `You chose ${(signalConfig?.options || [])[selectedIndex]}. Correct is ${(signalConfig?.options || [])[correctIndex]}.`)
                      : 'Response submitted.'}
                  </div>
                )
              ) : null}
              {!isGuidedSequenceSignal && revealAnswerVisible && (
                <section className="rounded-2xl border border-green-200 bg-green-50 p-4 space-y-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-green-700">Expected answer</p>
                  {revealAnswer?.kind === 'mcq_single' && (
                    <p className="text-sm text-green-900">
                      Correct option: {(revealAnswer.options || [])[Number(revealAnswer.correct_index)] ?? `#${revealAnswer.correct_index}`}
                    </p>
                  )}
                  {revealAnswer?.kind === 'mcq_multi' && (
                    <ul className="text-sm text-green-900 list-disc pl-5">
                      {(revealAnswer.correct_indices || []).map((idx: number) => (
                        <li key={`correct-${idx}`}>
                          {(revealAnswer.options || [])[idx] ?? `#${idx}`}
                        </li>
                      ))}
                    </ul>
                  )}
                  {revealAnswer?.kind === 'text' && (
                    <pre className="bg-gray-900 text-green-200 p-3 rounded text-sm overflow-x-auto">
                      <code>{String(revealAnswer.answer_code || '')}</code>
                    </pre>
                  )}
                  {revealAnswer?.kind === 'matching' && (
                    <ul className="text-sm text-green-900 list-disc pl-5">
                      {Object.entries(revealAnswer.pairs || {}).map(([left, right]) => (
                        <li key={`pair-${left}`}>{left} -&gt; {String(right)}</li>
                      ))}
                    </ul>
                  )}
                  {revealAnswer?.kind === 'ordering' && (
                    <div className="space-y-2">
                      {orderingCheck.status !== null && (
                        <div className="space-y-1">
                          <div className="inline-flex items-center gap-2">
                            <span className="text-sm font-semibold">Result:</span>
                            {orderingCheck.status ? (
                              <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                                Correct
                              </span>
                            ) : (
                              <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800">
                                Incorrect
                              </span>
                            )}
                          </div>

                          {!orderingCheck.status && orderingCheck.mismatchIndex !== null && (
                            <p className="text-xs text-green-900">
                              Mismatch at #{orderingCheck.mismatchIndex}
                            </p>
                          )}
                        </div>
                      )}

                      <p className="text-sm font-semibold text-green-900">Correct order</p>
                      <ol className="text-sm text-green-900 list-decimal pl-5">
                        {(Array.isArray(revealAnswer.answer) ? revealAnswer.answer : []).map((item: any, idx: number) => (
                          <li key={`order-${idx}`}>{String(item)}</li>
                        ))}
                      </ol>

                      {orderingStudentResponse.length > 0 && (
                        <>
                          <p className="text-sm font-semibold text-green-900">Your order</p>
                          <ol className="text-sm text-green-900 list-decimal pl-5">
                            {orderingStudentResponse.map((item: string, idx: number) => (
                              <li key={`your-order-${idx}`}>{String(item)}</li>
                            ))}
                          </ol>
                        </>
                      )}
                    </div>
                  )}
                  {revealAnswer?.kind === 'drag_drop_fill_blanks' && (
                    <div className="space-y-2 text-sm text-green-900">
                      <p>Answer: {Array.isArray(revealAnswer.answer) ? revealAnswer.answer.join(', ') : String(revealAnswer.answer || '')}</p>
                      {revealAnswer.code ? (
                        <pre className="bg-gray-900 text-green-200 p-3 rounded text-sm overflow-x-auto">
                          <code>{String(revealAnswer.code)}</code>
                        </pre>
                      ) : null}
                    </div>
                  )}
                  {revealAnswer?.kind === 'drag_drop_keywords' && (
                    <div className="space-y-2 text-sm text-green-900">
                      <p>Answer: {Array.isArray(revealAnswer.answer) ? revealAnswer.answer.join(', ') : String(revealAnswer.answer || '')}</p>
                      {revealAnswer.code_skeleton ? (
                        <pre className="bg-gray-900 text-green-200 p-3 rounded text-sm overflow-x-auto">
                          <code>{String(revealAnswer.code_skeleton)}</code>
                        </pre>
                      ) : null}
                    </div>
                  )}
                </section>
              )}
              {reasoningEnabled && (
                <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">Reasoning gate</p>
                    {reasoningRequired && (
                      <p className="mt-2 text-sm text-indigo-800">
                        Explain why you chose your answer ({reasoningMinChars}+ characters).
                      </p>
                    )}
                  </div>
                  {reasoningOptional && (
                    <label className="flex items-center gap-2 text-sm text-indigo-900">
                      <input
                        type="checkbox"
                        checked={showOptionalReasoning}
                        onChange={(e) => setShowOptionalReasoning(e.target.checked)}
                        disabled={!canSubmit || interactionLocked}
                      />
                      Add an explanation (optional)
                    </label>
                  )}
                  {shouldShowReasoningTextarea && (
                    <textarea
                      className="w-full border border-indigo-200 rounded px-3 py-2 text-sm"
                      rows={3}
                      value={reasoningText}
                      onChange={(e) => setReasoningText(e.target.value)}
                      placeholder="Explain your reasoning"
                      disabled={!canSubmit || interactionLocked}
                    />
                  )}
                </section>
              )}
              {confidenceRequired && (
              <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5 space-y-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-indigo-700">How confident are you?</p>
                  <p className="mt-2 text-sm text-indigo-800">Select one level before submitting.</p>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { value: 1, label: '1 — Guessing' },
                    { value: 2, label: '2 — Not sure' },
                    { value: 3, label: '3 — Somewhat sure' },
                    { value: 4, label: '4 — Mostly confident' },
                    { value: 5, label: '5 — Very confident' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setConfidenceLevel(option.value)}
                      className={`px-3 py-1 rounded border text-sm ${confidenceLevel === option.value ? 'border-indigo-500 bg-white text-indigo-700' : 'border-indigo-200 bg-white text-indigo-800'}`}
                      disabled={!canSubmit || interactionLocked}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </section>
              )}
              {isStudioStep && (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Need help?</p>
                      <p className="mt-1 text-sm text-amber-900">
                        Ask for support directly so your teacher sees it in Independent Work.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="rounded border border-amber-300 bg-white px-3 py-1.5 text-xs text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                      onClick={() => setStudioHelpPanelOpen((prev) => !prev)}
                      disabled={studioHelpSubmitting}
                    >
                      {studioHelpRequest?.status && studioHelpRequest.status !== 'none' ? 'Update help request' : 'Request help'}
                    </button>
                  </div>
                  {studioHelpRequest?.status === 'acknowledged' && (
                    <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Teacher saw your request.
                    </div>
                  )}
                  {studioHelpNotice && (
                    <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      {studioHelpNotice}
                    </div>
                  )}
                  {studioHelpError && (
                    <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      {studioHelpError}
                    </div>
                  )}
                  {studioHelpPanelOpen && (
                    <div className="rounded border border-amber-200 bg-white p-3 space-y-3">
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-amber-700" htmlFor="studio-help-kind">
                          Help type
                        </label>
                        <select
                          id="studio-help-kind"
                          className="mt-2 w-full rounded border border-amber-200 px-3 py-2 text-sm"
                          value={studioHelpKind}
                          onChange={(event) => setStudioHelpKind(event.target.value as typeof studioHelpKind)}
                          disabled={studioHelpSubmitting}
                        >
                          <option value="stuck">I’m stuck</option>
                          <option value="dont_understand_prompt">I don’t understand the prompt</option>
                          <option value="want_feedback">I want feedback</option>
                          <option value="technical_issue">I’m having a technical issue</option>
                          <option value="other">Something else</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.16em] text-amber-700" htmlFor="studio-help-note">
                          Note for your teacher
                        </label>
                        <textarea
                          id="studio-help-note"
                          className="mt-2 w-full rounded border border-amber-200 px-3 py-2 text-sm"
                          rows={3}
                          value={studioHelpNote}
                          onChange={(event) => setStudioHelpNote(event.target.value)}
                          disabled={studioHelpSubmitting}
                          placeholder="Optional: explain what you need help with."
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded bg-amber-900 px-4 py-2 text-sm text-white disabled:opacity-50"
                          onClick={() => { void submitStudioHelpRequest(false); }}
                          disabled={studioHelpSubmitting}
                        >
                          {studioHelpSubmitting ? 'Saving…' : (studioHelpRequest?.status && studioHelpRequest.status !== 'none' ? 'Update request' : 'Request help')}
                        </button>
                        {studioHelpRequest?.status && studioHelpRequest.status !== 'none' && studioHelpRequest.status !== 'resolved' && (
                          <button
                            type="button"
                            className="rounded border border-amber-300 bg-white px-4 py-2 text-sm text-amber-900 disabled:opacity-50"
                            onClick={() => { void submitStudioHelpRequest(true); }}
                            disabled={studioHelpSubmitting}
                          >
                            I’m okay now
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              )}
              <button
                type="button"
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
                onClick={submitSignal}
                disabled={signalDisabled}
              >
                {signalSubmitting
                  ? 'Submitting…'
                  : (shouldAutoAdvanceStudio
                    ? `${isGuidedSequenceSignal ? guidedSubmitLabel : 'Submit response'} and continue`
                    : (shouldFinishStudioOnSubmit
                      ? `${isGuidedSequenceSignal ? guidedSubmitLabel : 'Submit response'} and finish`
                      : (isGuidedSequenceSignal ? guidedSubmitLabel : 'Submit response')))
                }
              </button>
            </div>
          </section>
        )}

        {isFinalArtifactCard && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Final artifact</p>
                <p className="text-sm text-amber-900">This is your official Apply artifact.</p>
                {shouldShowExternalArtifactCard && (
                  <p className="text-sm text-amber-800">Submit this in Headrush.</p>
                )}
                {artifactPrompt && (
                  <p className="text-sm text-amber-800 whitespace-pre-wrap">{artifactPrompt}</p>
                )}
                {!artifactPrompt && shouldShowInternalArtifactCard && (
                  <p className="text-sm text-amber-800">
                    {evidenceInstructions || 'Submit your official Apply artifact.'}
                  </p>
                )}
              </div>
              {currentArtifact && (
                <span className={`text-xs px-2 py-1 rounded ${artifactStatusClassName}`}>
                  {artifactStatusLabel}
                </span>
              )}
            </div>

            {shouldShowArtifactContinuityCopy && (
              <div className="text-sm text-amber-900 bg-white border border-amber-200 rounded p-3">
                The class has moved on. You can still submit your official artifact.
              </div>
            )}

            {artifactError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {artifactError}
              </div>
            )}

            {artifactNotice && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                {artifactNotice}
              </div>
            )}

            {currentArtifact?.teacher_note && (
              <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-900">
                <span className="font-semibold">Teacher note:</span> {currentArtifact.teacher_note}
              </div>
            )}

            {currentArtifact?.coach_mode === 'optional' && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded border border-amber-300 text-amber-700 text-sm disabled:opacity-50"
                    onClick={() => {
                      setCoachPanelOpen((prev) => !prev);
                      setCoachError('');
                      setCoachNotice('');
                    }}
                    disabled={coachSubmitting || currentArtifact?.status === 'submitted' || currentArtifact?.status === 'under_review' || currentArtifact?.status === 'approved'}
                  >
                    {coachPanelOpen ? 'Hide draft coach' : 'Check my draft before I submit'}
                  </button>
                </div>

                {coachPanelOpen && (
                  <div className="rounded-lg border border-amber-200 bg-white p-4 space-y-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Draft coach</p>
                      <p className="mt-2 text-sm text-amber-900">
                        Paste a short draft summary, a link, or both. AI feedback is advisory. Your teacher decides approval.
                      </p>
                    </div>
                    <textarea
                      className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                      rows={4}
                      value={coachDraftText}
                      onChange={(e) => setCoachDraftText(e.target.value)}
                      placeholder="Paste a short summary of what your artifact does and what a reviewer should notice."
                      disabled={coachSubmitting}
                    />
                    <input
                      type="url"
                      className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                      value={coachDraftLink}
                      onChange={(e) => setCoachDraftLink(e.target.value)}
                      placeholder="Optional draft link https://..."
                      disabled={coachSubmitting}
                    />
                    {coachError && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                        {coachError}
                      </div>
                    )}
                    {coachNotice && (
                      <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                        {coachNotice}
                      </div>
                    )}
                    <button
                      type="button"
                      className="px-4 py-2 rounded border border-amber-400 text-amber-800 text-sm disabled:opacity-50"
                      onClick={submitDraftCoach}
                      disabled={!canRunDraftCoach || !isCoachLinkValid || coachSubmitting}
                    >
                      {coachSubmitting ? 'Checking…' : 'Run draft check'}
                    </button>
                  </div>
                )}

                {latestCoachSession && (
                  <div className="rounded-lg border border-amber-200 bg-white p-4 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Latest draft check</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${latestCoachSession.status === 'clear' ? 'bg-emerald-100 text-emerald-700' : latestCoachSession.status === 'flagged' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-700'}`}>
                        {latestCoachSession.status}
                      </span>
                      {latestCoachSession.updated_at && (
                        <span className="text-xs text-amber-700">{new Date(latestCoachSession.updated_at).toLocaleString()}</span>
                      )}
                    </div>
                    {latestCoachSession.student_feedback && (
                      <p className="text-sm text-amber-900">{latestCoachSession.student_feedback}</p>
                    )}
                    {latestCoachSession.draft_summary && (
                      <p className="text-xs text-amber-800">
                        Summary: {latestCoachSession.draft_summary}
                      </p>
                    )}
                    {Array.isArray(latestCoachSession.suggestions) && latestCoachSession.suggestions.length > 0 && (
                      <ul className="list-disc pl-5 text-sm text-amber-900">
                        {latestCoachSession.suggestions.slice(0, 3).map((suggestion, idx) => (
                          <li key={`coach-suggestion-${idx}`}>{suggestion}</li>
                        ))}
                      </ul>
                    )}
                    {Array.isArray(latestCoachSession.reason_codes) && latestCoachSession.reason_codes.length > 0 && (
                      <p className="text-xs text-amber-700">
                        Coach flags: {latestCoachSession.reason_codes.join(', ')}
                      </p>
                    )}
                    {latestCoachSession.ai_confidence !== undefined && latestCoachSession.ai_confidence !== null && (
                      <p className="text-xs text-amber-700">
                        AI confidence: {Math.round(Number(latestCoachSession.ai_confidence) * 100)}%
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {shouldShowExternalArtifactCard && (
              <div className="space-y-3">
                <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-900 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-amber-600">Artifact status</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${artifactStatusClassName}`}>
                      {artifactStatusLabel}
                    </span>
                    {artifactSubmittedTimestamp && (
                      <span className="text-xs text-amber-700">{artifactSubmittedTimestamp}</span>
                    )}
                  </div>
                  {artifactExternalAssignmentId && (
                    <p className="text-sm text-amber-900">Headrush reference: {artifactExternalAssignmentId}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-3">
                  {artifactExternalUrl ? (
                    <button
                      type="button"
                      className="px-4 py-2 rounded bg-amber-600 text-white text-sm disabled:opacity-50"
                      onClick={openExternalArtifactSubmission}
                      disabled={artifactActionSubmitting === 'drafting'}
                    >
                      {artifactActionSubmitting === 'drafting' ? 'Opening…' : 'Open Headrush submission'}
                    </button>
                  ) : (
                    <div className="text-sm text-amber-800">
                      Open Headrush with the assignment reference above.
                    </div>
                  )}
                  <button
                    type="button"
                    className="px-4 py-2 rounded border border-amber-400 text-amber-800 text-sm disabled:opacity-50"
                    onClick={markExternalArtifactSubmitted}
                    disabled={
                      artifactActionSubmitting === 'submitted'
                      || currentArtifact?.status === 'approved'
                      || currentArtifact?.status === 'under_review'
                    }
                  >
                    {artifactActionSubmitting === 'submitted'
                      ? 'Saving…'
                      : (shouldAutoAdvanceStudio
                        ? 'I submitted this in Headrush and continue'
                        : (shouldFinishStudioOnSubmit
                          ? 'I submitted this in Headrush and finish'
                          : 'I submitted this in Headrush'))}
                  </button>
                </div>
                <p className="text-xs text-amber-700">AI feedback is advisory. Your teacher decides approval.</p>
              </div>
            )}

            {shouldShowInternalArtifactCard && (
              <div className="space-y-3">
                {latestEvidence && (
                  <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-900 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-wide text-amber-600">{inRunSubmissionCopy.latestLabel}</span>
                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                        {latestEvidence.status}
                      </span>
                      {latestEvidenceTimestamp && (
                        <span className="text-xs text-amber-700">{latestEvidenceTimestamp}</span>
                      )}
                    </div>
                    {latestEvidence.content?.text && (
                      <p className="text-sm text-amber-900 whitespace-pre-wrap">{latestEvidence.content.text}</p>
                    )}
                    {latestEvidence.content?.url && (
                      <a
                        href={latestEvidence.content.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-amber-700 underline break-all"
                      >
                        {latestEvidence.content.url}
                      </a>
                    )}
                  </div>
                )}
                {isEvidenceLink ? (
                  <input
                    type="url"
                    className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                    value={evidenceLink}
                    onChange={(e) => setEvidenceLink(e.target.value)}
                    placeholder="https://"
                    disabled={evidenceDisabled}
                  />
                ) : (
                  <textarea
                    className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                    rows={3}
                    value={evidenceText}
                    onChange={(e) => setEvidenceText(e.target.value)}
                    placeholder={inRunSubmissionCopy.placeholder}
                    disabled={evidenceDisabled}
                  />
                )}
                {evidenceError && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                    {evidenceError}
                  </div>
                )}
                <button
                  type="button"
                  className="px-4 py-2 rounded bg-amber-600 text-white text-sm disabled:opacity-50"
                  onClick={submitEvidence}
                  disabled={evidenceDisabled || !evidenceValue || !isValidEvidenceLink || draftMatchesLatest}
                >
                  {evidenceSubmitting
                    ? 'Submitting…'
                    : (shouldAutoAdvanceStudio
                      ? `${inRunSubmissionCopy.submitButtonLabel} and continue`
                      : (shouldFinishStudioOnSubmit
                        ? `${inRunSubmissionCopy.submitButtonLabel} and finish`
                        : inRunSubmissionCopy.submitButtonLabel))}
                </button>
                {draftMatchesLatest && !evidenceDisabled && (
                  <p className="text-xs text-amber-700">No changes since last submission.</p>
                )}
              </div>
            )}
          </section>
        )}

        {shouldShowLegacyEvidenceCard && (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 space-y-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-amber-700">Legacy evidence</p>
              <p className="mt-2 text-sm text-amber-800">
                {evidenceInstructions || 'This step still uses the older in-run evidence flow.'}
              </p>
            </div>

            {latestEvidence && (
              <div className="rounded-lg border border-amber-200 bg-white p-3 text-sm text-amber-900 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs uppercase tracking-wide text-amber-600">{inRunSubmissionCopy.latestLabel}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                    {latestEvidence.status}
                  </span>
                  {latestEvidenceTimestamp && (
                    <span className="text-xs text-amber-700">{latestEvidenceTimestamp}</span>
                  )}
                </div>
                {latestEvidence.content?.text && (
                  <p className="text-sm text-amber-900 whitespace-pre-wrap">{latestEvidence.content.text}</p>
                )}
                {latestEvidence.content?.url && (
                  <a
                    href={latestEvidence.content.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-amber-700 underline break-all"
                  >
                    {latestEvidence.content.url}
                  </a>
                )}
              </div>
            )}
            <div className="space-y-3">
              {isEvidenceLink ? (
                <input
                  type="url"
                  className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                  value={evidenceLink}
                  onChange={(e) => setEvidenceLink(e.target.value)}
                  placeholder="https://"
                  disabled={evidenceDisabled}
                />
              ) : (
                <textarea
                  className="w-full border border-amber-200 rounded px-3 py-2 text-sm"
                  rows={3}
                  value={evidenceText}
                  onChange={(e) => setEvidenceText(e.target.value)}
                  placeholder={inRunSubmissionCopy.placeholder}
                  disabled={evidenceDisabled}
                />
              )}
              {evidenceError && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  {evidenceError}
                </div>
              )}
              <button
                type="button"
                className="px-4 py-2 rounded bg-amber-600 text-white text-sm disabled:opacity-50"
                onClick={submitEvidence}
                disabled={evidenceDisabled || !evidenceValue || !isValidEvidenceLink || draftMatchesLatest}
              >
                {evidenceSubmitting
                  ? 'Submitting…'
                  : (shouldAutoAdvanceStudio
                    ? `${inRunSubmissionCopy.submitButtonLabel} and continue`
                    : (shouldFinishStudioOnSubmit
                      ? `${inRunSubmissionCopy.submitButtonLabel} and finish`
                      : inRunSubmissionCopy.submitButtonLabel))}
              </button>
              {draftMatchesLatest && !evidenceDisabled && (
                <p className="text-xs text-amber-700">No changes since last submission.</p>
              )}
            </div>
          </section>
        )}
        </fieldset>
      </main>
    </div>
  );

  if (!redesignEnabled || embeddedClassroom) return presentationContent;

  return (
    <AppShell
      role={shellRole}
      navItems={shellNavItems}
      studentSectionId={data?.run?.section_id}
      actions={shellActions}
      title={shellTitle}
      className="max-w-none px-0 py-0"
    >
      {presentationContent}
    </AppShell>
  );
};
